"use client"

import React, { useState, useEffect, useRef } from "react"
import { Link, useParams, useNavigate, Routes, Route } from "react-router-dom"
import { Helmet } from "react-helmet-async"
import { useSwipeable } from "react-swipeable"
import { Heart, Star, X, ChevronLeft, User, Clock, ChevronRight, Calendar, Users2, BedDouble, Ruler, ArrowLeft, ArrowRight, Share, Home, ChefHat,Bed,Waves,Users } from 'lucide-react'
import { Button } from "../components/ui/button"
import { Label } from "../components/ui/label"
import { Textarea } from "../components/ui/textarea"
import { MaterialIcon } from "./MaterialIcon"
import AdCard from "./AdCard"
import {
  ref,
  onValue,
  push,
  set,
  update,
  remove,
  query,
  orderByChild,
  equalTo,
  limitToFirst,
  runTransaction,
  get,
  serverTimestamp,
  increment,
} from "../lib/firebase-database"
import Footer from "../components/Footer"
import { db, auth } from "../firebase"
import { useAuth } from "../hooks/useAuth"
import LoadingAnimation from "./LoadingAnimation"

import LogoutConfirmationPopup from "./LogoutConfirmationPopup"
import { AnimatePresence, motion } from "framer-motion"

import Logo from "../components/Logo"
import LoginPopup from "../components/login-popup"
import { ChevronDownIcon } from "@heroicons/react/24/solid"
import GoogleMap from "./GoogleMap"
import MobileImageGallery from "./MobileImageGallery"
import CheckAvailabilityModal from "../components/CheckAvailabilityModal"
import FeaturedShorts from "../components/YoutubeShort"
import WhatsAppEnquiryForm from "../components/WhatsappEnquiryForm"
import OptimizedImage, { getOptimizedUrl } from "./OptimizedImage"
import AvatarCircle from "./AvatarCircle"
import sadFaceIcon from "../assets/icons/icons8-sad-48.png"

// Add this new component at the top of the file
const VillaDetailsSkeleton = () => (
  <div className="animate-pulse">
    {/* Hero Image Skeleton */}
    <div className="w-full h-[300px] bg-gray-200 rounded-b-[20px]"></div>

    {/* Thumbnail Grid Skeleton */}
    <div className="grid grid-cols-2 gap-2 mt-2 px-2">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="h-[140px] bg-gray-200 rounded-[10px]"></div>
      ))}
    </div>

    {/* Villa Info Skeleton */}
    <main className="relative pt-4 px-4 space-y-2 pb-20">
      <div className="bg-white p-4 rounded-lg">
        <div className="h-8 bg-gray-200 rounded w-3/4 mb-3"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="flex gap-2">
          <div className="h-4 bg-gray-200 rounded w-24"></div>
          <div className="h-4 bg-gray-200 rounded w-24"></div>
        </div>
      </div>

      {/* Facilities Skeleton */}
      <div className="bg-white p-4 rounded-lg">
        <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
        <div className="flex gap-2 overflow-x-auto">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex-shrink-0 w-20 h-20 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      </div>

      {/* Description Skeleton */}
      <div className="bg-white p-4 rounded-lg">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-4 bg-gray-200 rounded w-full mb-2"></div>
        ))}
      </div>
    </main>

    {/* Bottom Bar Skeleton */}
    <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t p-4 z-50">
      <div className="flex justify-between items-center">
        <div className="space-y-2">
          <div className="h-4 bg-gray-200 rounded w-20"></div>
          <div className="h-6 bg-gray-200 rounded w-24"></div>
        </div>
        <div className="h-10 bg-gray-200 rounded-full w-32"></div>
      </div>
    </div>
  </div>
)

// Add this utility function at the top of the file
const calculateAverageRating = (reviews) => {
  if (!reviews || reviews.length === 0) return 0
  const sum = reviews.reduce((acc, review) => acc + Number(review.rating), 0)
  return Number((sum / reviews.length).toFixed(1))
}

// Keep all your existing utility functions (getTimeDifference, Popup, ImageGallery, getFacilityIcon) unchanged
const getTimeDifference = (timestamp) => {
  const now = new Date()
  const then = new Date(timestamp)
  const diffInSeconds = Math.floor((now - then) / 1000)
  if (diffInSeconds < 60) return `${diffInSeconds} seconds ago`
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`
  if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`
  return `${Math.floor(diffInSeconds / 31536000)} years ago`
}

const Popup = ({ isOpen, onClose, title, children }) => {
  React.useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm"
          onClick={onClose}
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"></div>
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 relative z-10 shadow-xl"
            style={{ maxHeight: "90vh", display: "flex", flexDirection: "column" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
              <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="overflow-y-auto custom-scrollbar flex-1 -mr-2 pr-2">
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const ImageGallery = ({ images, isOpen, onClose, initialIndex }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex || 0)

  React.useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden"
    else document.body.style.overflow = "unset"
    return () => {
      document.body.style.overflow = "unset"
    }
  }, [isOpen])

  const nextImage = () => setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length)
  const prevImage = () => setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length)

  const handlers = useSwipeable({
    onSwipedLeft: nextImage,
    onSwipedRight: prevImage,
  })

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-75 backdrop-blur-sm"></div>
      <div className="relative w-full h-full flex items-center justify-center" {...handlers}>
        <Button variant="ghost" size="icon" className="absolute top-4 right-4 text-white" onClick={onClose}>
          <X className="h-6 w-6" />
        </Button>
        <Button variant="ghost" size="icon" className="absolute left-4 text-white md:block hidden" onClick={prevImage}>
          <ChevronLeft className="h-6 w-6" />
        </Button>
        <img
          src={images[currentIndex] || "/placeholder.svg"}
          alt={`Villa image ${currentIndex + 1}`}
          className="max-w-full max-h-full object-contain"
        />
        <Button variant="ghost" size="icon" className="absolute right-4 text-white md:block hidden" onClick={nextImage}>
          <ChevronRight className="h-6 w-6" />
        </Button>
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white">
          {currentIndex + 1} / {images.length}
        </div>
      </div>
    </div>
  )
}

// Remove the existing getFacilityIcon function and replace it with this:

const getFacilityIcon = (facility) => {
  // Handle both old string format and new object format
  const facilityId = typeof facility === 'string' ? facility : facility.id
  const isPaid = typeof facility === 'object' ? facility.isPaid : false

  const facilityDetails = {
    wifi: { icon: <MaterialIcon name="wifi" className="text-gray-800 text-2xl" />, label: "WiFi" },
    tv: { icon: <MaterialIcon name="tv" className="text-gray-800 text-2xl" />, label: "Television" },
    ac: { icon: <MaterialIcon name="ac_unit" className="text-gray-800 text-2xl" />, label: "AC" },
    kitchen: { icon: <MaterialIcon name="kitchen" className="text-gray-800 text-2xl" />, label: "Kitchen" },
    parking: {
      icon: <MaterialIcon name="local_parking" className="text-gray-800 text-2xl" />,
      label: "Parking Available",
    },
    petFriendly: { icon: <MaterialIcon name="pets" className="text-gray-800 text-2xl" />, label: "Pet Friendly" },
    pool: { icon: <MaterialIcon name="pool" className="text-gray-800 text-2xl" />, label: "Swimming Pool" },
    gym: { icon: <MaterialIcon name="fitness_center" className="text-gray-800 text-2xl" />, label: "Gym" },
    gamesRoom: { icon: <MaterialIcon name="sports_esports" className="text-gray-800 text-2xl" />, label: "Games Room" },
    garden: { icon: <MaterialIcon name="yard" className="text-gray-800 text-2xl" />, label: "Garden Area" },
    mountainView: {
      icon: <MaterialIcon name="landscape" className="text-gray-800 text-2xl" />,
      label: "Mountain View",
    },
    beachView: { icon: <MaterialIcon name="beach_access" className="text-gray-800 text-2xl" />, label: "Beach View" },
    alcoholAllowed: {
      icon: <MaterialIcon name="local_bar" className="text-gray-800 text-2xl" />,
      label: "Alcohol Permitted",
    },
    noDrinking: { icon: <MaterialIcon name="no_drinks" className="text-gray-800 text-2xl" />, label: "No Alcohol" },
    birthday: { icon: <MaterialIcon name="cake" className="text-gray-800 text-2xl" />, label: "Birthday Events" },
    party: { icon: <MaterialIcon name="celebration" className="text-gray-800 text-2xl" />, label: "Party Allowed" },
    indoorGames: {
      icon: <MaterialIcon name="sports_kabaddi" className="text-gray-800 text-2xl" />,
      label: "Indoor Games",
    },
    bbq: { icon: <MaterialIcon name="outdoor_grill" className="text-gray-800 text-2xl" />, label: "BBQ Available" },
    meditation: {
      icon: <MaterialIcon name="self_improvement" className="text-gray-800 text-2xl" />,
      label: "Meditation Area",
    },
    spa: { icon: <MaterialIcon name="spa" className="text-gray-800 text-2xl" />, label: "Spa Services" },
    cottages: { icon: <MaterialIcon name="cottage" className="text-gray-800 text-2xl" />, label: "Private Cottages" },
    bungalow: { icon: <MaterialIcon name="home" className="text-gray-800 text-2xl" />, label: "Bungalow" },
    kingBed: { icon: <MaterialIcon name="king_bed" className="text-gray-800 text-2xl" />, label: "King Size Bed" },
    golfCourse: { icon: <MaterialIcon name="golf_course" className="text-gray-800 text-2xl" />, label: "Golf Course" },
    hiking: { icon: <MaterialIcon name="hiking" className="text-gray-800 text-2xl" />, label: "Hiking Trails" },
    turf: { icon: <MaterialIcon name="sports_cricket" className="text-gray-800 text-2xl" />, label: "Sports Turf" },
    cctv: { icon: <MaterialIcon name="videocam" className="text-gray-800 text-2xl" />, label: "CCTV Security" },
    powerBackup: { icon: <MaterialIcon name="bolt" className="text-gray-800 text-2xl" />, label: "Power Backup" },
    preWedding: {
      icon: <MaterialIcon name="video_camera_front" className="text-gray-800 text-2xl" />,
      label: "Pre-Wedding Shoots",
    },
    wedding: { icon: <MaterialIcon name="face_3" className="text-gray-800 text-2xl" />, label: "Wedding Venue" },
    bigFunction: { icon: <MaterialIcon name="groups" className="text-gray-800 text-2xl" />, label: "Large Functions" },
    towels: { icon: <MaterialIcon name="dry_cleaning" className="text-gray-800 text-2xl" />, label: "Fresh Towels" },
    dinner: { icon: <MaterialIcon name="restaurant" className="text-gray-800 text-2xl" />, label: "Dinner Service" },
    train: { icon: <MaterialIcon name="train" className="text-gray-800 text-2xl" />, label: "Train Station" },
    bus: { icon: <MaterialIcon name="directions_bus" className="text-gray-800 text-2xl" />, label: "Bus Station" },
  }

  const facilityInfo = facilityDetails[facilityId] || {
    icon: <MaterialIcon name="help_outline" className="text-gray-800 text-2xl" />,
    label: facilityId,
  }

  // Return both icon, label, and paid status
  return {
    ...facilityInfo,
    isPaid: isPaid
  }
}

export default function VillaDetailPage() {
  const [guestCount, setGuestCount] = useState(1)
  const [isAboutPopupOpen, setIsAboutPopupOpen] = useState(false)
  const [isFacilitiesPopupOpen, setIsFacilitiesPopupOpen] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)
  const [showStickyBar, setShowStickyBar] = useState(true)
  const [isFavorite, setIsFavorite] = useState(false)
  const [isOwnerPreview, setIsOwnerPreview] = useState(false)
  const [currentManagerIndex, setCurrentManagerIndex] = useState(0)
  const [isReviewPopupOpen, setIsReviewPopupOpen] = useState(false)
  const [newReview, setNewReview] = useState({ rating: 0, comment: "" })
  const [villaData, setVillaData] = useState(null)
  const [reviews, setReviews] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isInWishlist, setIsInWishlist] = useState(false)
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [isPaused, setIsPaused] = useState(false)
  const [averageRating, setAverageRating] = useState(0)
  const [showAllReviews, setShowAllReviews] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const ratingColors = ["#FF0000", "#FF7F00", "#FFD700", "#9ACD32", "#228B22"]
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [recommendedVillas, setRecommendedVillas] = useState([])
  const [currentRecommendedIndex, setCurrentRecommendedIndex] = useState(0)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false)
  const [isOwnerView, setIsOwnerView] = useState(false)
  const [hasUserReviewed, setHasUserReviewed] = useState(false)
  const [currentLocationImageIndex, setCurrentLocationImageIndex] = useState(0)
  const [isLocationImagesPaused, setIsLocationImagesPaused] = useState(false)
  const [checkInDate, setCheckInDate] = useState(null)
  const [checkOutDate, setCheckOutDate] = useState(null)
  const [bookingError, setBookingError] = useState("")
  const [initialMessage, setInitialMessage] = useState("")
  const [expandedReviews, setExpandedReviews] = useState({})
  const [showBookingForm, setShowBookingForm] = useState(false)
  const [isLocationGalleryOpen, setIsLocationGalleryOpen] = useState(false)
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false)
  const facilitiesButtonRef = useRef(null)
  const aboutButtonRef = useRef(null)
  const reviewButtonRef = useRef(null)
  const enquiryRef = useRef(null)
  const reviewsRef = useRef(null)
  const timerRef = useRef(null)
  const locationImagesTimerRef = useRef(null)
  const [showFullDescription, setShowFullDescription] = useState(false)
  const [selectedReview, setSelectedReview] = useState(null)
  const [userPhotos, setUserPhotos] = useState({})
  const [isRulesPopupOpen, setIsRulesPopupOpen] = useState(false)
  const [showPricingPopup, setShowPricingPopup] = useState(false)
  const [selectedMealImage, setSelectedMealImage] = useState(null)
  const [showAllPricing, setShowAllPricing] = useState(false)
  const [showWhatsAppForm, setShowWhatsAppForm] = useState(false)
  const [weather, setWeather] = useState(null)
  const [isWeatherLoading, setIsWeatherLoading] = useState(false)
  const [weatherError, setWeatherError] = useState(null)


   const fetchWeather = async () => {
  if (!villaData?.location) return;
  
  let { lat, lng } = villaData.location || {};

  // Convert to numbers if they're strings
  lat = typeof lat === "string" ? parseFloat(lat) : lat;
  lng = typeof lng === "string" ? parseFloat(lng) : lng;

  // Check for valid coordinates
  if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
    console.log("Invalid coordinates:", { lat, lng });
    setWeatherError("Invalid location coordinates");
    return;
  }

  console.log("Using coordinates:", { lat, lng });
  const apiKey = "6c713f14e95219e24354f5b29cb6bec5";
  if (!apiKey) {
    setWeatherError("Weather API key missing");
    return;
  }

  try {
    setIsWeatherLoading(true);
    setWeatherError(null);

    // First try current weather to test API key
    const currentWeatherUrl = `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lng}&units=metric&appid=${apiKey}`;
    console.log("Fetching current weather:", currentWeatherUrl);
    
    const currentRes = await fetch(currentWeatherUrl);
    console.log("Current weather response status:", currentRes.status);
    
    if (!currentRes.ok) {
      const errorText = await currentRes.text();
      console.error("Current weather API error:", errorText);
      throw new Error(`Weather API error: ${currentRes.status} - ${errorText}`);
    }
    
    const currentData = await currentRes.json();
    console.log("Current weather data:", currentData);

    // Try forecast for daily data
    const forecastUrl = `https://api.openweathermap.org/data/2.5/forecast?lat=${lat}&lon=${lng}&units=metric&appid=${apiKey}`;
    console.log("Fetching forecast:", forecastUrl);
    
    const forecastRes = await fetch(forecastUrl);
    console.log("Forecast response status:", forecastRes.status);
    
    if (!forecastRes.ok) {
      const errorText = await forecastRes.text();
      console.error("Forecast API error:", errorText);
      // Use only current weather if forecast fails
      setWeather({
        current: {
          temp: currentData.main.temp,
          feels_like: currentData.main.feels_like,
          humidity: currentData.main.humidity,
          wind_speed: currentData.wind.speed,
          weather: currentData.weather
        },
        daily: [] // Empty daily array
      });
      return;
    }

    const forecastData = await forecastRes.json();
    console.log("Forecast data:", forecastData);

    // Process forecast data to create daily summaries
    const dailyMap = {};
    const today = new Date().toDateString();
    
    // Add current weather as today
    dailyMap[today] = {
      dt: Math.floor(Date.now() / 1000),
      temp: {
        max: currentData.main.temp_max || currentData.main.temp,
        min: currentData.main.temp_min || currentData.main.temp
      },
      weather: currentData.weather
    };

    // Process forecast data
    forecastData.list.forEach(item => {
      const date = new Date(item.dt * 1000).toDateString();
      if (!dailyMap[date]) {
        dailyMap[date] = {
          dt: item.dt,
          temp: { max: item.main.temp, min: item.main.temp },
          weather: item.weather
        };
      } else {
        // Update min/max temperatures
        dailyMap[date].temp.max = Math.max(dailyMap[date].temp.max, item.main.temp);
        dailyMap[date].temp.min = Math.min(dailyMap[date].temp.min, item.main.temp);
      }
    });

    const daily = Object.values(dailyMap).slice(0, 8); // Today + next 7 days

    setWeather({
      current: {
        temp: currentData.main.temp,
        feels_like: currentData.main.feels_like,
        humidity: currentData.main.humidity,
        wind_speed: currentData.wind.speed,
        weather: currentData.weather
      },
      daily: daily
    });

    console.log("Weather data set successfully");

  } catch (e) {
    console.error("Weather fetch error:", e);
    setWeatherError(`Failed to load weather: ${e.message}`);
  } finally {
    setIsWeatherLoading(false);
  }
};

// Re-run when villa data changes
useEffect(() => {
  if (villaData?.location?.lat && villaData?.location?.lng) {
    console.log("Villa data loaded, fetching weather...");
    fetchWeather();
  }
}, [villaData]);

  const mealImageSwipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (selectedMealImage && villaData?.meals?.images) {
        const nextIndex = (selectedMealImage.index + 1) % villaData.meals.images.length
        setSelectedMealImage({ src: villaData.meals.images[nextIndex], index: nextIndex })
      }
    },
    onSwipedRight: () => {
      if (selectedMealImage && villaData?.meals?.images) {
        const prevIndex = (selectedMealImage.index - 1 + villaData.meals.images.length) % villaData.meals.images.length
        setSelectedMealImage({ src: villaData.meals.images[prevIndex], index: prevIndex })
      }
    },
  })

  // Add state for user data
  const [userData, setUserData] = useState({})

  // Add useEffect to listen for user data changes
  useEffect(() => {
    const usersRef = ref(db, "users")
    const unsubscribe = onValue(usersRef, (snapshot) => {
      const users = snapshot.val()
      if (users) {
        setUserData(users)
      }
    })

    return () => unsubscribe()
  }, [])

  useEffect(() => {
    if (showLoginPopup) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [showLoginPopup]);

  // Add useEffect to listen for user photo changes
  useEffect(() => {
    const usersRef = ref(db, "users")
    const unsubscribe = onValue(usersRef, (snapshot) => {
      const users = snapshot.val()
      if (users) {
        const photos = {}
        Object.entries(users).forEach(([userId, userData]) => {
          if (userData.photoURL) {
            photos[userId] = userData.photoURL
          }
        })
        setUserPhotos(photos)
      }
    })

    return () => unsubscribe()
  }, [])

  let ratingColor = ratingColors[0]
  if (averageRating >= 2 && averageRating < 3) ratingColor = ratingColors[1]
  else if (averageRating >= 3 && averageRating < 4) ratingColor = ratingColors[2]
  else if (averageRating >= 4 && averageRating < 5) ratingColor = ratingColors[3]
  else if (averageRating >= 5) ratingColor = ratingColors[4]

  const { villaId } = useParams()
  const { user, isAdmin, isOwner } = useAuth()
  const navigate = useNavigate()
  const managementMembers = (villaData?.management || []).filter(
    (member) => member && (Boolean(member.image) || Boolean(member?.name?.trim())),
  )
  const hasManagementDetails = managementMembers.length > 0
  const activeManager = hasManagementDetails
    ? managementMembers[currentManagerIndex % managementMembers.length]
    : null

  // Keep all your existing hooks and functions unchanged
  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (managementMembers.length > 1) {
        setCurrentManagerIndex((prevIndex) => (prevIndex + 1) % managementMembers.length)
        setIsPaused(true)
        setTimeout(() => setIsPaused(false), 5000)
      }
    },
    onSwipedRight: () => {
      if (managementMembers.length > 1) {
        setCurrentManagerIndex(
          (prevIndex) => (prevIndex - 1 + managementMembers.length) % managementMembers.length,
        )
        setIsPaused(true)
        setTimeout(() => setIsPaused(false), 5000)
      }
    },
  })

  useEffect(() => {
    if (user && villaData) {
      const isOwner = user.email === villaData.ownerEmail
      setIsOwnerPreview(isOwner)
      if (isOwner && window.location.pathname !== `/villa/${villaId}`) navigate("/owner")
    }
  }, [user, villaData, navigate])

  const handleLogoClick = (e) => {
    if (isOwnerPreview) {
      e.preventDefault()
      navigate("/owner")
    }
  }

  const handleProfileClick = (e) => {
    if (isOwnerPreview) {
      e.preventDefault()
      navigate("/owner")
    } else {
      goToProfile()
    }
  }

  const handleShareClick = () => {
    if (villaData) {
      const message = `Check out this amazing villa: ${villaData.name} at ${window.location.href}`
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`
      const instagramUrl = villaData.instagramProfile || "https://instagram.com"
      window.open(whatsappUrl, "_blank")
      setTimeout(() => window.open(instagramUrl, "_blank"), 500)
    }
  }

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    if (isChatOpen) document.body.style.overflow = "hidden"
    else document.body.style.overflow = "unset"
    return () => {
      document.body.style.overflow = "unset"
    }
  }, [isChatOpen])

  const fetchReviews = async () => {
    try {
      const reviewsRef = ref(db, `reviews/${villaId}`)
      const snapshot = await get(reviewsRef)
      if (snapshot.exists()) {
        const reviewsData = snapshot.val()
        const reviewsList = Object.entries(reviewsData).map(([id, data]) => ({
          id,
          ...data,
        }))
        setReviews(reviewsList.sort((a, b) => b.createdAt - a.createdAt))
      }
    } catch (error) {
      console.error("Error fetching reviews:", error)
    }
  }

  const [cityBlogs, setCityBlogs] = useState([])

  useEffect(() => {
    const fetchCityBlogs = async () => {
      if (villaData?.mainCity) {
        const cityBlogsRef = ref(db, `cityBlogs/${villaData.mainCity.toLowerCase().replace(/\s+/g, '-')}`)
        const snapshot = await get(cityBlogsRef)
        if (snapshot.exists()) {
          const blogs = Object.entries(snapshot.val()).map(([id, blog]) => ({
            id,
            ...blog
          }))
          setCityBlogs(blogs)
        }
      }
    }

    if (villaData?.mainCity) {
      fetchCityBlogs()
    }
  }, [villaData?.mainCity])

  const fetchRecommendations = async () => {
    try {
      if (!villaData?.mainCity) return

      const villasRef = ref(db, "villas")
      const recommendationsQuery = query(
        villasRef,
        orderByChild("mainCity"),
        equalTo(villaData.mainCity),
        limitToFirst(5),
      )

      const snapshot = await get(recommendationsQuery)
      if (snapshot.exists()) {
        const villas = snapshot.val()
        const recommendedVillasArray = Object.entries(villas)
          .filter(([key]) => key !== villaId)
          .map(([key, value]) => ({ id: key, ...value }))
        setRecommendedVillas(recommendedVillasArray)
      }
    } catch (error) {
      console.error("Error fetching recommendations:", error)
    }
  }

  const updateVisitorAnalytics = async () => {
    if (!villaId) return;

    const today = new Date();
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).getTime();
    const startOfWeek = new Date(today.getTime() - (7 * 24 * 60 * 60 * 1000)).getTime();
    const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1).getTime();

    try {
      const analyticsRef = ref(db, `analytics/visitors/${villaId}`);
      await runTransaction(analyticsRef, (currentData) => {
        const data = currentData || {
          today: 0,
          lastSevenDays: 0,
          thisMonth: 0,
          lastMonth: 0,
          lastVisit: 0
        };

        // Reset counters if needed
        if (data.lastVisit < startOfDay) {
          data.today = 0;
        }
        if (data.lastVisit < startOfMonth) {
          data.thisMonth = 0;
        }
        if (data.lastVisit < startOfLastMonth) {
          data.lastMonth = data.thisMonth;
          data.thisMonth = 0;
        }

        // Update counters
        data.today = (data.today || 0) + 1;
        data.lastSevenDays = (data.lastSevenDays || 0) + 1;
        data.thisMonth = (data.thisMonth || 0) + 1;
        data.lastVisit = Date.now();

        return data;
      });
    } catch (error) {
      console.error("Error updating visitor analytics:", error);
    }
  };

  useEffect(() => {
    const fetchVillaData = async () => {
      setLoading(true)
      try {
        const villaRef = ref(db, `villas/${villaId}`)
        const snapshot = await get(villaRef)

        if (snapshot.exists()) {
          const data = snapshot.val()
          setVillaData(data)

          // Update visitor analytics after fetching villa data
          await updateVisitorAnalytics()

          // Fetch reviews and recommendations (no longer blocking on image preloads)
          await Promise.all([fetchReviews(), fetchRecommendations()])
        } else {
          setError("Villa not found")
        }
      } catch (error) {
        console.error("Error fetching villa data:", error)
        setError("Failed to load villa details")
      } finally {
        setLoading(false)
      }
    }

    fetchVillaData()
  }, [villaId])

  useEffect(() => {
    if (user && villaId) {
      const wishlistRef = ref(db, `wishlists/${user.uid}/${villaId}`)
      onValue(wishlistRef, (snapshot) => setIsInWishlist(snapshot.exists()))
    }
  }, [user, villaId])

  useEffect(() => {
    if (reviews.length > 0) {
      // Calculate average rating
      const avgRating = calculateAverageRating(reviews)
      setAverageRating(avgRating)

      // Update villa data with new rating
      if (villaData && villaId) {
        const villaRef = ref(db, `villas/${villaId}`)
        update(villaRef, {
          averageRating: avgRating,
          reviewCount: reviews.length,
        })
      }
    } else {
      setAverageRating(0)
    }
  }, [reviews, villaId, villaData])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }, [])

  useEffect(() => {
    if (user && villaData && user.email === villaData.ownerEmail) setIsOwnerView(true)
  }, [user, villaId, villaData])

  useEffect(() => {
    if (user && reviews.length > 0) {
      setHasUserReviewed(reviews.some((review) => review.userId === user.uid))
    }
  }, [user, reviews])

  const toggleWishlist = async () => {
    if (!user) {
      setShowLoginPopup(true)
      return
    }

    const wasInWishlist = isInWishlist
    setIsInWishlist(!wasInWishlist)

    const wishlistRef = ref(db, `wishlists/${user.uid}/${villaId}`)
    try {
      if (wasInWishlist) await remove(wishlistRef)
      else await set(wishlistRef, true)
    } catch (error) {
      console.error("Wishlist update failed:", error)
      setIsInWishlist(wasInWishlist)
      alert("Failed to update favorites. Please try again.")
    }
  }

  const handleAddressClick = () => {
    if (!user) {
      alert("Please login to view location on map");
      return;
    }
    if (villaData.googleMapsLink) {
      // If we have a direct Google Maps link, use it as is
      window.open(villaData.googleMapsLink, "_blank")
    } else if (villaData.location?.lat && villaData.location?.lng) {
      // If we have coordinates, use them with the villa name
      const googleMapsUrl = `https://www.google.com/maps/search/${encodeURIComponent(villaData.name)}/@${villaData.location.lat},${villaData.location.lng},17z`
      window.open(googleMapsUrl, "_blank")
    } else {
      // Fallback to address search with villa name
      const googleMapsUrl = `https://www.google.com/maps/search/${encodeURIComponent(villaData.name + " " + villaData.address)}`
      window.open(googleMapsUrl, "_blank")
    }
  }

  const scrollToReviews = () => reviewsRef.current?.scrollIntoView({ behavior: "smooth" })

  const handleReviewSubmit = () => {
    if (!user) {
      alert("You must be logged in to submit a review.")
      return
    }
    if (hasUserReviewed) {
      alert("You have already submitted a review for this villa.")
      return
    }
    if (!newReview.rating) {
      alert("Please select a rating before submitting.")
      return
    }
    if (!newReview.comment.trim()) {
      alert("Please write a review comment before submitting.")
      return
    }

    const reviewData = {
      userId: user.uid,
      userName: user.displayName,
      userPhoto: user.photoURL,
      rating: newReview.rating,
      comment: newReview.comment,
      createdAt: Date.now(),
      reply: null,
    }

    const reviewsRef = ref(db, `reviews/${villaId}`)
    const villaReviewsRef = ref(db, `villas/${villaId}/reviews`)
    const villaRef = ref(db, `villas/${villaId}`)
    const newReviewRef = push(reviewsRef)

    Promise.all([
      set(newReviewRef, reviewData),
      set(push(villaReviewsRef), reviewData),
      runTransaction(villaRef, (currentData) => {
        if (!currentData) return currentData
        const reviews = currentData.reviews || {}
        const reviewCount = (currentData.reviewCount || 0) + 1
        const totalRating = (currentData.averageRating || 0) * (reviewCount - 1) + newReview.rating
        return {
          ...currentData,
          reviews: { ...reviews, [newReviewRef.key]: reviewData },
          averageRating: totalRating / reviewCount,
          reviewCount: reviewCount,
        }
      }),
    ])
      .then(() => {
        setHasUserReviewed(true)
        setIsReviewPopupOpen(false)
        setNewReview({ rating: 0, comment: "" })
      })
      .catch((error) => {
        console.error("Error submitting review:", error)
        alert("Failed to submit review. Please try again.")
      })
  }

  const handleReplySubmit = (reviewId, replyText) => {
    if (!user) {
      alert("You must be logged in to reply to reviews.")
      return
    }
    if (!replyText.trim()) {
      alert("Please enter a reply before submitting.")
      return
    }

    const reviewRef = ref(db, `reviews/${villaId}/${reviewId}`)
    update(reviewRef, {
      replies: {
        [Date.now()]: {
          text: replyText,
          userId: user.uid,
          userName: user.displayName,
          userPhoto: user.photoURL,
          createdAt: Date.now(),
        },
      },
    })
      .then(() => {
        const updatedReviews = reviews.map((review) =>
          review.id === reviewId
            ? {
              ...review,
              replies: {
                ...review.replies,
                [Date.now()]: {
                  text: replyText,
                  userId: user.uid,
                  userName: user.displayName,
                  userPhoto: user.photoURL,
                  createdAt: Date.now(),
                },
              },
              replyText: "",
            }
            : review,
        )
        setReviews(updatedReviews)
      })
      .catch((error) => {
        console.error("Error updating review:", error)
        alert("Failed to submit reply. Please try again.")
      })
  }

  useEffect(() => {
    const handleScroll = () => {
      if (enquiryRef.current) {
        const rect = enquiryRef.current.getBoundingClientRect()
        setShowStickyBar(rect.top > window.innerHeight)
      }
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const scrollToEnquiry = () => enquiryRef.current?.scrollIntoView({ behavior: "smooth" })

  const goToProfile = () => {
    navigate("/profile")
    setShowProfileMenu(false)
  }

  const goToAdminDashboard = () => {
    navigate("/admin")
    setShowProfileMenu(false)
  }

  const handleLogoutClick = () => {
    setShowLogoutConfirmation(true)
    setShowProfileMenu(false)
  }

  const handleLogoutConfirm = () => {
    auth.signOut()
    setShowLogoutConfirmation(false)
    navigate("/")
  }

  const handleLogoutCancel = () => setShowLogoutConfirmation(false)

  useEffect(() => {
    if (managementMembers.length > 1 && !isPaused) {
      timerRef.current = setInterval(() => {
        setCurrentManagerIndex((prevIndex) => (prevIndex + 1) % managementMembers.length)
      }, 2000)
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [managementMembers.length, isPaused])

  useEffect(() => {
    // Add a style tag to ensure content doesn't overflow
    const style = document.createElement("style")
    style.innerHTML = `
      body, html {
        max-width: 100vw;
        overflow-x: hidden;
      }
      * {
        max-width: 100vw;
      }
    `
    document.head.appendChild(style)

    return () => {
      document.head.removeChild(style)
    }
  }, [])

  useEffect(() => {
    const fetchRecommendedVillas = async () => {
      const villasRef = ref(db, "villas")
      const villasQuery = query(villasRef, orderByChild("mainCity"), equalTo(villaData.mainCity), limitToFirst(5))
      onValue(villasQuery, (snapshot) => {
        const villas = snapshot.val()
        if (villas) {
          const recommendedVillasArray = Object.entries(villas)
            .filter(([key]) => key !== villaId)
            .map(([key, value]) => ({ id: key, ...value }))
          setRecommendedVillas(recommendedVillasArray)
        }
      })
    }
    if (villaData) fetchRecommendedVillas()
  }, [villaData, villaId])

  const handleSwipeLeft = () => {
    if (isMobile) setCurrentRecommendedIndex((prevIndex) => (prevIndex + 1) % recommendedVillas.length)
    else if (currentRecommendedIndex < recommendedVillas.length - 4)
      setCurrentRecommendedIndex((prevIndex) => prevIndex + 1)
  }

  const handleSwipeRight = () => {
    if (isMobile)
      setCurrentRecommendedIndex((prevIndex) => (prevIndex - 1 + recommendedVillas.length) % recommendedVillas.length)
    else if (currentRecommendedIndex > 0) setCurrentRecommendedIndex((prevIndex) => prevIndex - 1)
  }

  const recommendedSwipeHandlers = useSwipeable({
    onSwipedLeft: handleSwipeLeft,
    onSwipedRight: handleSwipeRight,
  })

  useEffect(() => {
    if (villaData?.locationImages?.length > 1 && !isLocationImagesPaused) {
      locationImagesTimerRef.current = setInterval(() => {
        setCurrentLocationImageIndex((prev) => (prev + 1) % villaData.locationImages.length)
      }, 3000)
    }
    return () => {
      if (locationImagesTimerRef.current) clearInterval(locationImagesTimerRef.current)
    }
  }, [villaData, isLocationImagesPaused])

  const locationSwipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      setCurrentLocationImageIndex((prev) => (prev + 1) % (villaData?.locationImages?.length || 1))
      setIsLocationImagesPaused(true)
      setTimeout(() => setIsLocationImagesPaused(false), 5000)
    },
    onSwipedRight: () => {
      setCurrentLocationImageIndex(
        (prev) => (prev - 1 + (villaData?.locationImages?.length || 1)) % (villaData?.locationImages?.length || 1),
      )
      setIsLocationImagesPaused(true)
      setTimeout(() => setIsLocationImagesPaused(false), 5000)
    },
  })

  const validateBookingDetails = () => {
    if (!guestCount || guestCount < 1) {
      setBookingError("Please select number of guests")
      return false
    }
    if (!checkInDate || !checkOutDate) {
      setBookingError("Please select check-in and check-out dates")
      return false
    }
    return true
  }

  const handleChatClick = () => {
    if (!user) {
      alert("Please login to chat with the owner")
      return
    }
    if (user && villaId) {
      const analyticsRef = ref(db, `analytics/bookNow/${villaId}`);
      runTransaction(analyticsRef, (currentCount) => {
        return (currentCount || 0) + 1;
      });
    }
    if (!guestCount || guestCount < 1) {
      setBookingError("Please select number of guests")
      scrollToEnquiry()
      return
    }
    if (!checkInDate || !checkOutDate) {
      setBookingError("Please select check-in and check-out dates")
      scrollToEnquiry()
      return
    }
    const formattedCheckIn = checkInDate.toLocaleDateString()
    const formattedCheckOut = checkOutDate.toLocaleDateString()
    const message = `Hi! I'm interested in booking ${villaData.name} for ${guestCount} guests between ${formattedCheckIn} to ${formattedCheckOut}. Could you please provide more information about availability?`
    setInitialMessage(message)
    setIsChatOpen(true)
  }

  // New functions from your mobile UI
  const toggleExpand = (reviewId) => {
    setExpandedReviews((prev) => ({
      ...prev,
      [reviewId]: !prev[reviewId],
    }))
  }

  const handleReviewClick = () => {
    if (!user) {
      alert("You must be logged in to write a review.")
      return
    }
    if (hasUserReviewed) {
      alert("You have already submitted a review for this villa.")
      return
    }
    setIsReviewPopupOpen(true)
  }

  const getRatingColor = (rating) => {
    switch (rating) {
      case 5:
        return "bg-green-500"
      case 4:
        return "bg-green-400"
      case 3:
        return "bg-yellow-400"
      case 2:
        return "bg-orange-400"
      case 1:
        return "bg-red-400"
      default:
        return "bg-gray-200"
    }
  }

  const FacilityCard = ({ facilityInfo }) => {
    return (
      <div className="flex-shrink-0 w-20 bg-white p-1 rounded-lg flex flex-col items-center text-center group relative">
        <div className="relative">
          {facilityInfo.isPaid && (
            <div className="absolute -top-1 -right-1 bg-amber-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold shadow-sm border border-white z-10">
              ₹
            </div>
          )}
          <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
            {facilityInfo.icon}
          </div>
        </div>
        <span className="text-xs text-gray-800 w-full break-words mt-1">
          {facilityInfo.label}
        </span>
      </div>
    );
  };

  // Add this function to handle "View All Reviews" click
  const handleViewAllReviews = () => {
    navigate(`/reviews/${villaId}`, {
      state: {
        reviews: reviews,
        villaName: villaData.name,
        isOwner: user?.email === villaData.ownerEmail,
        isAdmin: isAdmin,
      },
    })
  }
  useEffect(() => {
    const shouldPreventScroll =
      showLoginPopup ||
      isChatOpen ||
      showBookingForm ||
      isLocationGalleryOpen ||
      isGalleryOpen ||
      isAboutPopupOpen ||
      isFacilitiesPopupOpen ||
      isReviewPopupOpen ||
      isRulesPopupOpen ||
      selectedMealImage ||
      showAvailabilityModal ||
      showPricingPopup ||
      showWhatsAppForm

    if (shouldPreventScroll) {
      document.body.style.overflow = "hidden"
      document.body.style.position = "fixed"
      document.body.style.width = "100%"
    } else {
      document.body.style.overflow = "unset"
      document.body.style.position = "static"
      document.body.style.width = "auto"
    }

    return () => {
      document.body.style.overflow = "unset"
      document.body.style.position = "static"
      document.body.style.width = "auto"
    }
  }, [
    showLoginPopup,
    isChatOpen,
    showBookingForm,
    isLocationGalleryOpen,
    isGalleryOpen,
    isAboutPopupOpen,
    isFacilitiesPopupOpen,
    isReviewPopupOpen,
    isRulesPopupOpen,
    selectedMealImage,
    showWhatsAppForm,
    showAvailabilityModal,
    showPricingPopup,
  ])

  if (loading) {
    return <VillaDetailsSkeleton />
  }

  if (isLoading) return <LoadingAnimation />
  if (error) return <div className="flex justify-center items-center h-screen text-red-500">{error}</div>
  if (!villaData) return <div className="flex justify-center items-center h-screen">Villa not found</div>

  const toggleMobileMenu = () => setIsMenuOpen(!isMenuOpen)

  const handleHeroImageClick = () => {
    if (isMobile) {
      navigate("/photos", {
        state: {
          images: villaData.images,
          villaName: villaData.name,
          guestCount: villaData.guestCount,
          bedrooms: villaData.bedrooms,
          totalVillaArea: villaData.totalVillaArea,
        },
      })
    } else {
      setIsGalleryOpen(true)
    }
  }

  const handleViewAllPhotos = () => {
    navigate(`/villa/${villaId}/photos`, {
      state: {
        images: villaData.images,
        villaName: villaData.name,
        guestCount: villaData.guestCount,
        bedrooms: villaData.bedrooms,
        totalVillaArea: villaData.totalVillaArea,
      },
    })
  }

  // Add this function inside your component
  const handleDateSelect = (checkInDate, checkOutDate, guestCount) => {
    setCheckInDate(checkInDate)
    setCheckOutDate(checkOutDate)
    setGuestCount(guestCount)
    setBookingError("")
  }

  const siteUrl = "https://villakosh.in"
  const canonicalUrl = `${siteUrl}/villa/${villaId}`
  const villaName = villaData?.name || "Villa"
  const villaCity = villaData?.mainCity || "Maharashtra"
  const pageTitle = `${villaName} in ${villaCity} | VillaKosh`
  const pageDescription =
    (villaData?.description || `Book ${villaName} in ${villaCity} with verified host details, amenities, and instant booking support on VillaKosh.`)
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 160)
  const primaryImage = villaData?.images?.[0] || villaData?.thumbnail || `${siteUrl}/VillaKosh.png`
  const numericRate = Number(String(villaData?.rate ?? "").replace(/[^\d.]/g, ""))

  const breadcrumbSchema = {
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: siteUrl,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: `Villas in ${villaCity}`,
        item: `${siteUrl}/villas-in-${String(villaCity).toLowerCase().replace(/\s+/g, "-")}`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: villaName,
        item: canonicalUrl,
      },
    ],
  }

  const lodgingBusinessSchema = {
    "@type": "LodgingBusiness",
    name: villaName,
    description: pageDescription,
    url: canonicalUrl,
    image: (villaData?.images || []).slice(0, 8),
    address: {
      "@type": "PostalAddress",
      streetAddress: villaData?.address || "",
      addressLocality: villaCity,
      addressRegion: villaData?.state || "Maharashtra",
      addressCountry: "IN",
    },
    geo:
      villaData?.location?.lat && villaData?.location?.lng
        ? {
          "@type": "GeoCoordinates",
          latitude: villaData.location.lat,
          longitude: villaData.location.lng,
        }
        : undefined,
    aggregateRating:
      reviews?.length > 0
        ? {
          "@type": "AggregateRating",
          ratingValue: Number(averageRating || 0).toFixed(1),
          reviewCount: reviews.length,
        }
        : undefined,
    offers: Number.isFinite(numericRate)
      ? {
        "@type": "Offer",
        priceCurrency: "INR",
        price: numericRate,
        availability: "https://schema.org/InStock",
        url: canonicalUrl,
      }
      : undefined,
  }

  const webPageSchema = {
    "@type": "WebPage",
    name: pageTitle,
    description: pageDescription,
    url: canonicalUrl,
    inLanguage: "en-IN",
    primaryImageOfPage: {
      "@type": "ImageObject",
      url: primaryImage,
    },
  }

  const schemaGraph = {
    "@context": "https://schema.org",
    "@graph": [webPageSchema, breadcrumbSchema, lodgingBusinessSchema],
  }

  return (
    <div className={`min-h-screen bg-white font-sans overflow-x-hidden ${isChatOpen ? "overflow-hidden" : ""}`}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={primaryImage} />
        <meta property="og:site_name" content="VillaKosh" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={primaryImage} />

        <script type="application/ld+json">{JSON.stringify(schemaGraph)}</script>
      </Helmet>
      {isMobile ? (
        <>
          {/* Transparent Header */}
          <header className="absolute top-0 left-0 right-0 z-50">
            <div className="container mx-auto px-4">
              <div className="flex items-center justify-between py-4">
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center justify-center w-10 h-10 bg-white rounded-full"
                >
                  <ArrowLeft className="h-6 w-6 text-black" />
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={toggleWishlist}
                    className="flex items-center justify-center w-10 h-10 bg-white rounded-full"
                  >
                    <Heart className={`h-5 w-5 ${isInWishlist ? "fill-black text-black" : "text-black"}`} />
                  </button>
                  <button
                    onClick={handleShareClick}
                    className="flex items-center justify-center w-10 h-10 bg-white rounded-full"
                  >
                    <Share className="h-5 w-5 text-black" />
                  </button>
                  {user ? (
                    <button
                      onClick={() => navigate('/profile')}
                      className="flex items-center justify-center w-10 h-10 bg-white rounded-full overflow-hidden"
                    >
                      <AvatarCircle
                        src={user.photoURL}
                        name={user.displayName || user.email}
                        className="w-full h-full"
                        textClassName="text-xs"
                      />
                    </button>
                  ) : (
                    <button
                      onClick={() => setShowLoginPopup(true)}
                      className="flex items-center justify-center w-10 h-10 bg-white rounded-full"
                    >
                      <User className="h-5 w-5 text-black" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </header>

          {/* Image Gallery (Restored Original Structure) */}
          <div className="relative">
            {/* Main Large Image with Curved Bottom */}
            <div className="w-full h-[300px] overflow-hidden">
              <OptimizedImage
                src={villaData.images[0] || "/placeholder.svg"}
                alt={villaData.name}
                preset="heroMedium"
                eager
                className="w-full h-full object-cover rounded-b-[20px]"
                onClick={() => setCurrentIndex(0)}
              />
            </div>

            {/* Two Row Grid with Curved Edges and Gaps */}
            <div className="grid grid-cols-2 gap-2 mt-2 px-2">
              <div className="h-[140px]">
                <OptimizedImage
                  src={villaData.images[1] || "/placeholder.svg"}
                  alt={villaData.name}
                  preset="gallery"
                  className="w-full h-full object-cover rounded-[10px]"
                  onClick={() => setCurrentIndex(1)}
                />
              </div>
              <div className="h-[140px]">
                <OptimizedImage
                  src={villaData.images[2] || "/placeholder.svg"}
                  alt={villaData.name}
                  preset="gallery"
                  className="w-full h-full object-cover rounded-[10px]"
                  onClick={() => setCurrentIndex(2)}
                />
              </div>
              <div className="h-[140px]">
                <OptimizedImage
                  src={villaData.images[3] || "/placeholder.svg"}
                  alt={villaData.name}
                  preset="gallery"
                  className="w-full h-full object-cover rounded-[10px]"
                  onClick={() => setCurrentIndex(3)}
                />
              </div>
              <div className="h-[140px]">
                <OptimizedImage
                  src={villaData.images[4] || "/placeholder.svg"}
                  alt={villaData.name}
                  preset="gallery"
                  className="w-full h-full object-cover rounded-[10px]"
                  onClick={() => setCurrentIndex(4)}
                />
              </div>
            </div>

            {/* View All Photos Button */}
            <button
              onClick={handleViewAllPhotos}
              className="absolute bottom-4 right-4 bg-white px-4 py-2 rounded-full text-sm font-medium shadow-lg text-black"
            >
              View All Photos ({villaData.images.length})
            </button>
          </div>

          {/* Main Content with Reduced Gap */}
          <main className="relative pt-4 px-2 space-y-4 pb-20">
            {/* Villa Details - Aligned */}
            <div className="mb-2 bg-white p-4 rounded-lg">
              <div className="flex flex-col">
                <h2 className="text-[20px] font-bold mb-1">{villaData.name}</h2>
                <p className="text-gray-600 mb-2">
                  {villaData.mainCity}
                  {villaData.state && `, ${villaData.state}`}
                </p>

                {/* Updated horizontal scrollable details with Material Icons */}
                <div className="overflow-x-auto -mx-4 mb-2">
                  <div className="flex px-4 space-x-4 pb-2">
                    {[
                      { show: true, icon: "groups", text: `${villaData.guestCount} Guests` },
                      villaData.bedrooms && {
                        show: true,
                        icon: "bed",
                        text: `${villaData.bedrooms} Bedroom${villaData.bedrooms > 1 ? "s" : ""}`,
                      },
                      villaData.halls && {
                        show: true,
                        icon: "meeting_room",
                        text: `${villaData.halls} Hall${villaData.halls > 1 ? "s" : ""}`,
                      },
                      villaData.kitchens && {
                        show: true,
                        icon: "kitchen",
                        text: `${villaData.kitchens} Kitchen${villaData.kitchens > 1 ? "s" : ""}`,
                      },
                      villaData.poolCount > 0 && {
                        show: true,
                        icon: "pool",
                        text: `${villaData.poolCount} Pool${villaData.poolCount > 1 ? "s" : ""}`,
                      },
                      villaData.totalVillaArea && {
                        show: true,
                        icon: "crop_square",
                        text: `${villaData.totalVillaArea} Area`,
                      },
                    ]
                      .filter(Boolean)
                      .map((item, idx) => (
                        <div
                          key={idx}
                          className="flex-shrink-0 flex flex-col items-center min-w-[86px]"
                        >
                          <div className="w-[72px] h-[56px] bg-gray-100 rounded-lg flex items-center justify-center">
                            <MaterialIcon name={item.icon} className="text-gray-800 text-3xl" />
                          </div>
                          <span className="mt-2 text-xs font-medium text-gray-700 text-center whitespace-nowrap">
                            {item.text}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex justify-start -space-x-2 mr-2">
                    {reviews.slice(0, 4).map((review, idx) => (
                      <img
                        key={idx}
                        src={userPhotos[review.userId] || review.userPhoto || "/placeholder.svg"}
                        alt={`Reviewer ${idx + 1}`}
                        className="w-10 h-10 rounded-full border-2 border-white"
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`h-4 w-4 ${
                            i < Math.round(averageRating)
                              ? "text-yellow-400 fill-current"
                              : "text-gray-300"
                          }`}
                        />
                      ))}
                    </div>
                    <button
                      onClick={handleViewAllReviews}
                      className="text-sm text-gray-600 hover:text-gray-900"
                    >
                      {averageRating.toFixed(1)} ({reviews.length})
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Facilities - Restored Original Padding */}
            <div className="bg-white p-4 rounded-lg border-1 mb-2">
              <h3 className="text-[20px] font-bold mb-4">Facilities</h3>
              <div className="overflow-x-auto -mx-4 scrollbar-hide">
                <div className="flex pl-4 space-x-2 pb-4">
                  {villaData.facilities
                    ?.filter((facility) => {
                      const facilityId = typeof facility === 'string' ? facility : facility.id;
                      return facilityId !== "stationPickup" && facilityId !== "busStopPickup";
                    })
                    .map((facility, index) => (
                      <FacilityCard
                        key={index}
                        facilityInfo={getFacilityIcon(facility)}
                      />
                    ))}
                </div>
              </div>
              {villaData.facilities?.length > 5 && (
                <Button
                  variant="default"
                  onClick={() => setIsFacilitiesPopupOpen(true)}
                  className="bg-black text-white py-2 rounded-lg"
                >
                  See More
                </Button>
              )}
            </div>
            <Popup
              isOpen={isFacilitiesPopupOpen}
              onClose={() => setIsFacilitiesPopupOpen(false)}
              title="Facilities"
              buttonRef={facilitiesButtonRef}
            >
              <div className="p-4">
                {/* Add a legend for paid facilities */}
                <div className="mb-4 p-3 bg-gray-50 rounded-lg flex items-center gap-2">
                  <div className="bg-amber-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold">
                    ₹
                  </div>
                  <span className="text-sm text-gray-600">
                    These facilities are paid and not included in the package
                  </span>
                </div>

                {/* Facilities grid */}
                <div className="grid grid-cols-2 gap-6">
                  {villaData.facilities
                    .filter((facility) => facility !== "stationPickup" && facility !== "busStopPickup")
                    .map((facility, index) => {
                      const facilityInfo = getFacilityIcon(facility);
                      return (
                        <div key={index} className="flex items-center space-x-3">
                          <div className="relative">
                            {facilityInfo.isPaid && (
                              <div className="absolute -top-1 -right-1 bg-amber-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold shadow-sm border border-white">
                                ₹
                              </div>
                            )}
                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                              {facilityInfo.icon}
                            </div>
                          </div>
                          <span className="text-[14px] text-gray-700">{facilityInfo.label}</span>
                        </div>
                      );
                    })}
                </div>
              </div>
            </Popup>

            {/* About This Place & Management */}
            <div className="grid gap-2 mb-2">
              <div className="bg-white p-4 border-1  rounded-lg ">
                <h3 className="text-[20px] font-bold mb-4">About This Place</h3>
                <p className="text-gray-600 line-clamp-5 mb-4">{villaData.description}</p>
                <Button variant="default" onClick={() => setIsAboutPopupOpen(true)}>
                  Read More
                </Button>
              </div>
              {/* Management Section */}
              <div className="grid grid-cols-2 gap-2 mb-2">
                {/* Owner Square */}
                <div className="bg-white p-2 rounded-lg ">
                  <h3 className="text-lg font-bold text-center mb-4">Owner</h3>
                  <div className="flex flex-col items-center">
                    <div className="w-24 h-24 rounded-full overflow-hidden mb-3">
                      {villaData.owner?.image ? (
                        <img
                          src={villaData.owner.image || "/placeholder.svg"}
                          alt={villaData.owner.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-300">
                          <span className="text-3xl font-bold text-white">
                            {villaData.owner?.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                      )}
                    </div>
                    <p className="text-sm font-bold text-center">{villaData.owner?.name}</p>
                  </div>
                </div>

                {/* Management Square */}
                <div className="bg-white p-2 rounded-lg">
                  <h3 className="text-lg font-bold text-center mb-4">Management</h3>
                  <div className="flex flex-col items-center" {...handlers}>
                    <div className="w-24 h-24 rounded-full overflow-hidden mb-3 bg-gray-200">
                      {activeManager?.image ? (
                        <img
                          src={activeManager.image || "/placeholder.svg"}
                          alt={activeManager.name || "Management"}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-100">
                          <img src={sadFaceIcon} alt="Management details not shared" className="w-12 h-12 opacity-80" />
                        </div>
                      )}
                    </div>
                    <p className="text-sm font-bold text-center">{activeManager?.name || "Management details not shared"}</p>
                    {managementMembers.length > 1 && (
                      <div className="flex justify-center gap-2 mt-2">
                        {managementMembers.map((_, index) => (
                          <div
                            key={index}
                            className={`w-2 h-2 rounded-full ${index === currentManagerIndex ? "bg-gray-800" : "bg-gray-300"
                              }`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* About The Location - Updated Section */}
            <div className="bg-white p-4 rounded-lg border-1 mb-2">
              <h3 className="text-[20px] font-bold mb-4">About The Location</h3>
              <p className="text-gray-700 mb-6">{villaData.locationDescription}</p>

              {/* Location Images */}
              <div
                className="relative w-full max-w-[350px] h-[300px] mx-auto rounded-lg overflow-hidden mb-4"
                {...locationSwipeHandlers}
              >
                {villaData.locationImages && villaData.locationImages.length > 0 ? (
                  <div className="relative w-full h-full">
                    <div
                      className="absolute inset-0 transition-transform duration-300"
                      style={{ transform: `translateX(-${currentLocationImageIndex * 100}%)` }}
                    >
                      {villaData.locationImages.map((image, index) => (
                        <img
                          key={index}
                          src={image || "/placeholder.svg"}
                          alt={`Location ${index + 1}`}
                          className="absolute inset-0 w-full h-full object-cover"
                          style={{ left: `${index * 100}%`, width: "100%" }}
                        />
                      ))}
                    </div>
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
                      {villaData.locationImages.map((_, index) => (
                        <button
                          key={index}
                          className={`w-2 h-2 rounded-full transition-colors ${index === currentLocationImageIndex ? "bg-white" : "bg-white/50 hover:bg-white/75"}`}
                          onClick={() => {
                            setCurrentLocationImageIndex(index)
                            setIsLocationImagesPaused(true)
                            setTimeout(() => setIsLocationImagesPaused(false), 5000)
                          }}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full bg-gray-100 rounded-lg">
                    <p className="text-gray-500">No location images available</p>
                  </div>
                )}
              </div>

              {/* Horizontally Scrollable Location Highlights */}
              <div className="overflow-x-auto -mx-4 scrollbar-hide">
                <div className="flex gap-3 px-4 pb-2">
                  {villaData.locationHighlights.map((highlight, index) => (
                    <div
                      key={index}
                      className="flex-shrink-0 w-[280px] bg-gray-200 rounded-lg p-4 border border-gray-100"
                    >
                      <div className="flex items-start">
                        <div className="w-6 h-6 rounded-full bg-black flex items-center justify-center flex-shrink-0 mt-1">
                          <span className="text-white text-sm">{index + 1}</span>
                        </div>
                        <span className="flex-1 ml-3 text-gray-800">{highlight}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Transportation */}
            <div className="bg-white p-4 rounded-lg border-1 mb-2">
              <h3 className="text-[20px] font-bold mb-4">Nearest Transportation</h3>
              <div className="grid grid-cols-2 gap-4">
                {/* Train Station */}
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gray-100 rounded-lg">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      className="bi bi-train-front"
                      viewBox="0 0 16 16"
                    >
                      <path d="M5.621 1.485c1.815-.454 2.943-.454 4.758 0 .784.196 1.743.673 2.527 1.119.688.39 1.094 1.148 1.094 1.979V13.5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 13.5V4.583c0-.831.406-1.588 1.094-1.98.784-.445 1.744-.922 2.527-1.118m5-.97C8.647.02 7.353.02 5.38.515c-.924.23-1.982.766-2.78 1.22C1.566 2.322 1 3.432 1 4.582V13.5A2.5 2.5 0 0 0 3.5 16h9a2.5 2.5 0 0 0 2.5-2.5V4.583c0-1.15-.565-2.26-1.6-2.849-.797-.453-1.855-.988-2.779-1.22ZM5 13a1 1 0 1 1-2 0 1 1 0 0 1 2 0m0 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0m7 1a1 1 0 1 0-1-1 1 1 0 1 0-2 0 1 1 0 0 0 2 0 1 1 0 0 0 1 1M4.5 5a.5.5 0 0 0-.5.5v2a.5.5 0 0 0 .5.5h3V5zm4 0v3h3a.5.5 0 0 0 .5-.5v-2a.5.5 0 0 0-.5-.5zM3 5.5A1.5 1.5 0 0 1 4.5 4h7A1.5 1.5 0 0 1 13 5.5v2A1.5 1.5 0 0 1 11.5 9h-7A1.5 1.5 0 0 1 3 7.5zM6.5 2a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Train Station</p>
                    <p className="font-semibold">{villaData.trainDistance} km</p>
                    {!!villaData.stationPickup && (
                      <p className="text-sm text-green-600 mt-1">Pickup Available</p>
                    )}
                  </div>
                </div>

                {/* Bus Stop - existing code */}
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gray-100 rounded-lg">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      className="bi bi-bus-front"
                      viewBox="0 0 16 16"
                    >
                      <path d="M5 11a1 1 0 1 1-2 0 1 1 0 0 1 2 0m8 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0m-6-1a1 1 0 1 0 0 2h2a1 1 0 1 0 0-2zm1-6c-1.876 0-3.426.109-4.552.226A.5.5 0 0 0 3 4.723v3.554a.5.5 0 0 0 .448.497C4.574 8.891 6.124 9 8 9s3.426-.109 4.552-.226A.5.5 0 0 0 13 8.277V4.723a.5.5 0 0 0-.448-.497A44 44 0 0 0 8 4m0-1c-1.837 0-3.353.107-4.448.22a.5.5 0 1 1-.104-.994A44 44 0 0 1 8 2c1.876 0 3.426.109 4.552.226a.5.5 0 1 1-.104.994A43 43 0 0 0 8 3" />
                      <path d="M15 8a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1V2.64c0-1.188-.845-2.232-2.064-2.372A44 44 0 0 0 8 0C5.9 0 4.208.136 3.064.268 1.845.408 1 1.452 1 2.64V4a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1v3.5c0 .818.393 1.544 1 2v2a.5.5 0 0 0 .5.5h2a.5.5 0 0 0 .5-.5V14h6v1.5a.5.5 0 0 0 .5.5h2a.5.5 0 0 0 .5-.5v-2c.607-.456 1-1.182 1-2zM8 1c2.056 0 3.71.134 4.822.261.676.078 1.178.66 1.178 1.379v8.86a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 11.5V2.64c0-.72.502-1.301 1.178-1.379A43 43 0 0 1 8 1" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Bus Stop</p>
                    <p className="font-semibold">{villaData.busDistance} km</p>
                    {!!villaData.busStopPickup && <p className="text-sm text-green-600 mt-1">Pickup Available</p>}
                  </div>
                </div>
              </div>
            </div>
            {/* Check-in & Check-out Section */}
            <div className="bg-white p-4 rounded-lg border-1 mb-2">
              <h3 className="text-[20px] font-bold mb-4">Check-in & Check-out</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gray-100 rounded-lg">
                    <Clock className="h-6 w-6 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Check-in Time</p>
                    <p className="font-semibold">{villaData.checkInTime || "3:00 PM"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gray-100 rounded-lg">
                    <Clock className="h-6 w-6 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Check-out Time</p>
                    <p className="font-semibold">{villaData.checkOutTime || "11:00 AM"}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Spaces Section */}
            {villaData.spaces && villaData.spaces.length > 0 && (
              <div className="bg-white p-4 rounded-lg mb-2">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-[20px] font-bold">Interior & Amenities</h3>
                  <div className="flex items-center gap-2">
                  </div>
                </div>

                <div className="overflow-x-auto -mx-4 scrollbar-hide">
                  <div
                    className="flex gap-4 px-4"
                    {...useSwipeable}
                  >
                    {villaData.spaces.map((space, index) => (
                      <div
                        key={index}
                        className="flex-shrink-0 w-[300px] rounded-lg overflow-hidden border"
                      >
                        <div className="relative h-[200px]">
                          <img
                            src={space.image || "/placeholder.svg"}
                            alt={space.name}
                            className="w-full h-full object-cover rounded-lg"
                          />
                          {space.topLabel && (
                            <div className="absolute top-2 right-2 bg-white px-2 py-1 rounded-md text-sm font-medium">
                              {space.topLabel}
                            </div>
                          )}
                          {space.name && (
                            <div className="absolute bottom-2 left-2 bg-white px-2 py-1 rounded-md text-sm font-bold">
                              {space.name}
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <ul className="space-y-2">
                            {space.description?.map((desc, descIndex) => (
                              <li key={descIndex} className="flex items-start">
                                <span className="mr-2 text-lg">•</span>
                                <span className="flex-1 text-[14px] text-gray-800">{desc}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Meals Section */}
            <div className="bg-white p-4 rounded-lg border-1 mb-2">
              <h3 className="text-[20px] font-bold mb-4">Meals</h3>

              <p className="text-gray-600 mb-4">
                {villaData.meals?.description ||
                  "We provide healthy home-cooked meals including breakfast, lunch, and dinner"}
              </p>

              <div className="flex flex-wrap gap-4 mb-4">
                {villaData.meals?.types?.includes("veg") && (
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6">
                      <svg viewBox="0 0 24 24">
                        <rect width="20" height="20" x="2" y="2" fill="none" stroke="green" strokeWidth="2" />
                        <circle cx="12" cy="12" r="6" fill="green" />
                      </svg>
                    </div>
                    <span>Vegetarian</span>
                  </div>
                )}

                {villaData.meals?.types?.includes("nonveg") && (
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6">
                      <svg viewBox="0 0 24 24">
                        <rect width="20" height="20" x="2" y="2" fill="none" stroke="red" strokeWidth="2" />
                        <circle cx="12" cy="12" r="6" fill="red" />
                      </svg>
                    </div>
                    <span>Non-Vegetarian</span>
                  </div>
                )}

                {villaData.meals?.types?.includes("vegan") && (
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6">
                      <svg viewBox="0 0 24 24">
                        <rect width="20" height="20" x="2" y="2" fill="none" stroke="darkgreen" strokeWidth="2" />
                        <path d="M12 6l-6 12h12z" fill="darkgreen" />
                      </svg>
                    </div>
                    <span>Vegan</span>
                  </div>
                )}
              </div>

              <div className="overflow-x-auto -mx-4 scrollbar-hide">
                <div className="flex gap-2 px-4 pb-4">
                  {villaData.meals?.images?.map((image, index) => (
                    <div
                      key={index}
                      className="flex-shrink-0 w-[180px] h-[240px] rounded-lg overflow-hidden"
                      onClick={() =>
                        navigate("/meal-gallery", {
                          state: {
                            images: villaData.meals.images,
                            mealDescription: villaData.meals?.description || "Meal Image",
                          },
                        })
                      }
                    >
                      <img
                        src={image || "/placeholder.svg"}
                        alt={`Meal ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* Add Architectural Uniqueness section */}
            {villaData.architecturalUniqueness && (
              <div className="bg-white p-4 border-1  rounded-lg">
                <h3 className="text-[20px] font-bold mb-4">Architectural Uniqueness</h3>
                <p className="text-gray-600 mb-4">{villaData.architecturalUniqueness}</p>
              </div>
            )}

            <div className="bg-white p-4 rounded-lg border-1 mb-2">
              <h3 className="text-[20px] font-bold mb-4">Weather at {villaData.name}</h3>
              {isWeatherLoading && <p className="text-gray-500 text-sm">Loading weather...</p>}
              {weatherError && <p className="text-red-500 text-sm">{weatherError}</p>}
              {!isWeatherLoading && !weatherError && weather && (
                <div>
                  {weather.current && (
                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <img
                          src={`https://openweathermap.org/img/wn/${weather.current.weather?.[0]?.icon || "01d"}@2x.png`}
                          alt="Current"
                          className="w-14 h-14"
                        />
                        <div>
                          <p className="text-xl font-semibold">
                            {Math.round(weather.current.temp)}°C
                          </p>
                          <p className="text-sm text-gray-600 capitalize">
                            {weather.current.weather?.[0]?.description || ""}
                          </p>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        Feels like {Math.round(weather.current.feels_like)}°C<br />
                        Humidity {weather.current.humidity}%<br />
                        Wind {Math.round(weather.current.wind_speed)} m/s
                      </div>
                    </div>
                  )}
                  <div className="overflow-x-auto -mx-2 px-2">
                    <div className="flex gap-3">
                      {weather.daily.map((d, i) => {
                        const date = new Date(d.dt * 1000)
                        const label = i === 0 ? "Today" : date.toLocaleDateString(undefined, { weekday: "short" })
                        return (
                          <div
                            key={d.dt}
                            className="flex-shrink-0 w-[90px] bg-gray-50 rounded-lg p-3 text-center border"
                          >
                            <p className="text-xs font-medium mb-1">{label}</p>
                            <img
                              src={`https://openweathermap.org/img/wn/${d.weather?.[0]?.icon || "01d"}@2x.png`}
                              alt={d.weather?.[0]?.main || ""}
                              className="w-12 h-12 mx-auto -my-2"
                            />
                            <p className="text-xs capitalize line-clamp-1">
                              {d.weather?.[0]?.main || ""}
                            </p>
                            <p className="text-sm font-semibold mt-1">
                              {Math.round(d.temp.max)}° / {Math.round(d.temp.min)}°
                            </p>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Villa Rules */}
            <div className="bg-white p-4 rounded-lg border-1  mb-2">
              <h3 className="text-[20px] font-bold mb-4">Villa Rules</h3>
              <ul className="space-y-2">
                {villaData.rules?.slice(0, 6).map((rule, index) => (
                  <li key={index} className="flex items-start">
                    <span className="mr-2 text-lg">•</span>
                    <span className="flex-1 text-gray-700">{rule}</span>
                  </li>
                ))}
              </ul>
              {villaData.rules?.length > 3 && (
                <Button
                  variant="default"
                  onClick={() => setIsRulesPopupOpen(true)}
                  className="mt-4 bg-black text-white py-2 rounded-lg"
                >
                  See More
                </Button>
              )}
            </div>

            {/* Cancellation Policy - New Section */}
            {villaData.cancellationPolicy && (
              <div className="bg-white p-4 rounded-lg border-1 mb-2">
                <h3 className="text-[20px] font-bold mb-4">Cancellation Policy</h3>
                <div className="text-gray-600 whitespace-pre-line">{villaData.cancellationPolicy}</div>
              </div>
            )}

            {/* Featured Villa Shorts */}
            {villaData.youtubeShorts && villaData.youtubeShorts.length > 0 && (
              <FeaturedShorts
                shorts={villaData.youtubeShorts}
                youtubeLink={villaData.youtubeLink}
              />
            )}

            {/* Reviews - Aligned with Main Heading */}
            <div className="bg-white p-4 rounded-lg border-1 mb-2">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[20px] font-bold">Ratings and reviews</h3>
              </div>
              <div className="flex items-center gap-6 mb-4">
                <div className="text-center">
                  <div className="text-4xl font-bold" style={{ color: ratingColor }}>
                    {averageRating.toFixed(1)}
                  </div>
                  <div className="text-sm font-semibold">
                    {averageRating >= 4.5
                      ? "EXCELLENT"
                      : averageRating >= 4.0
                        ? "VERY GOOD"
                        : averageRating >= 3.0
                          ? "GOOD"
                          : averageRating >= 2.0
                            ? "FAIR"
                            : "POOR"}
                  </div>
                  <div className="text-sm text-gray-500">{reviews.length} ratings</div>
                </div>
                <div className="flex-1">
                  {[5, 4, 3, 2, 1].map((rating) => {
                    const count = reviews.filter((r) => r.rating === rating).length
                    const percentage = reviews.length > 0 ? (count / reviews.length) * 100 : 0
                    return (
                      <div key={rating} className="flex items-center gap-2 mb-1">
                        <span className="w-3">{rating}</span>
                        <Star className={`h-4 w-4 ${count > 0 ? "text-yellow-400" : "text-gray-400"}`} />
                        <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-300 ${getRatingColor(rating)}`}
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                        <span className="w-8 text-sm text-gray-500">{percentage.toFixed(0)}%</span>
                      </div>
                    )
                  })}
                </div>
              </div>
              <div className="overflow-x-scroll no-scrollbar">
                <div className="flex gap-2 px-1 pb-4">
                  {reviews.slice(0, 3).map((review) => (
                    <div
                      key={review.id}
                      className="flex-shrink-0 w-[250px] bg-white border-2 rounded-lg p-2 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => setSelectedReview(review)}
                    >
                      <div className="flex items-center mb-2">
                        <img
                          src={userPhotos[review.userId] || review.userPhoto || "/placeholder.svg"}
                          alt={userData[review.userId]?.displayName || review.userName}
                          className="w-10 h-10 rounded-full mr-2"
                        />
                        <div>
                          <p className="font-bold">{userData[review.userId]?.displayName || review.userName}</p>
                          <div className="flex items-center gap-2">
                            <div className="flex">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`h-4 w-4 ${i < review.rating ? "text-yellow-400 fill-current" : "text-gray-300"}`}
                                />
                              ))}
                            </div>
                            <span className="text-sm text-gray-500">{getTimeDifference(review.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                      <p className="px-12 text-gray-700 line-clamp-2">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </div>
              {/* Updated Review Actions Layout */}
              <div className="flex justify-between items-center gap-4 mt">
                <Button
                  onClick={handleReviewClick}
                  className="flex-1 bg-black border border-gray-200 hover:bg-gray-50"
                  disabled={hasUserReviewed}
                >
                  {hasUserReviewed ? "Already Reviewed" : "Write a Review"}
                </Button>

                {reviews.length > 3 && (
                  <Button
                    variant="outline"
                    className="flex-1 bg-white border border-gray-200 hover:bg-gray-50"
                    onClick={handleViewAllReviews}
                  >
                    View All ({reviews.length})
                  </Button>
                )}
              </div>
            </div>

            {/* New Location Map Section */}
            <div className="bg-white p-4 rounded-lg border-1 mb-2">
              <h3 className="text-[20px] font-bold mb-4">Location</h3>
              <div className="space-y-4">
                <div className="h-[300px] rounded-lg overflow-hidden">
                  {user ? (
                    villaData.location?.lat && villaData.location?.lng ? (
                      <GoogleMap
                        villaName={villaData.name}
                        address={villaData.address}
                        latitude={villaData.location?.lat}
                        longitude={villaData.location?.lng}
                        googleMapsLink={villaData.googleMapsLink}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100">
                        <p className="text-gray-500">Location map not available</p>
                      </div>
                    )
                  ) : (
                    <div className="relative w-full h-full">
                      <div className="absolute inset-0 backdrop-blur-[8px] bg-gray-100/50 flex items-center justify-center z-10">
                        <div className="text-center">
                          <p className="text-lg font-semibold mb-2">Login to see location</p>
                          <button
                            onClick={() => setShowLoginPopup(true)}
                            className="bg-black text-white px-4 py-2 rounded-full text-sm hover:bg-gray-800"
                          >
                            Login
                          </button>
                        </div>
                      </div>
                      <div className="w-full h-full bg-gray-200"></div>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-center">
                  <a
                    href={
                      villaData.googleMapsLink ||
                      `https://www.google.com/maps/search/${encodeURIComponent(villaData.name)}/@${villaData.location?.lat},${villaData.location?.lng},17z`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  ></a>
                </div>
              </div>
            </div>
            {/* Blog Description */}
            {cityBlogs.length > 0 && (
              <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                <h3 className="text-[20px] font-bold mb-4">Explore {villaData.mainCity}</h3>

                <div className="overflow-x-auto -mx-4 scrollbar-hide mb-6">
                  <div className="flex gap-4 px-4">
                    {cityBlogs.map((blog) => (
                      <div
                        key={blog.id}
                        className="flex-shrink-0 w-[300px] bg-gray-50 rounded-xl overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow"
                      >
                        <img
                          src={blog.images?.[0] || "/placeholder.svg"}
                          alt={blog.title}
                          className="w-full h-[200px] object-cover"
                        />
                        <div className="p-4">
                          <h4 className="font-bold text-lg mb-2 line-clamp-2">{blog.title}</h4>
                          <p className="text-gray-600 text-sm line-clamp-3">{blog.excerpt}</p>
                          <div className="mt-4 flex items-center justify-between">
                            <span className="text-sm text-gray-500">{blog.readTime} min read</span>
                            <button
                              onClick={() => window.open(blog.link, '_blank')}
                              className="text-blue-600 text-sm font-medium hover:underline"
                            >
                              Read More →
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}


            {/* Recommended Villas */}
            {recommendedVillas?.length > 0 && (
              <div className="bg-white p-4 rounded-lg border-1  mb-2">
                <h3 className="text-[20px] font-bold mb-4">Recommended Villas</h3>
                <div className="overflow-x-auto -mx-4 scrollbar-hide">
                  <div className="flex gap-2 px-4 pb-4">
                    {recommendedVillas.map((villa) => (
                      <Link
                        to={`/villa/${villa.id}`}
                        key={villa.id}
                        className="flex-shrink-0 w-[180px] bg-white rounded-[20px] overflow-hidden relative h-[240px]"
                      >
                        <div className="relative w-full h-full">
                          <img
                            src={villa.thumbnail || villa.images?.[0] || "/placeholder.svg"}
                            alt={villa.name}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/70 via-black/70 to-transparent" />
                          <button
                            onClick={(e) => {
                              e.preventDefault()
                              toggleWishlist()
                            }}
                            className="absolute top-4 right-4 z-10 bg-white p-1.5 rounded-full shadow-md hover:scale-110 transition-transform"
                          >
                            <Heart
                              className={`h-5 w-5 ${isInWishlist ? "text-red-500 fill-current" : "text-gray-600 hover:text-red-500"} transition-colors`}
                            />
                          </button>
                          <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                            <h3 className="text-base font-bold line-clamp-2 mb-1">{villa.name}</h3>
                            <div className="flex items-center justify-between">
                              <p className="text-base font-bold">
                                ₹{villa.rate}
                                <span className="text-sm font-normal opacity-90"> onwords</span>
                              </p>
                              <div className="flex items-center">
                                <Star className="h-3.5 w-3.5 text-yellow-400" />
                                <span className="ml-1">
                                  {villa.averageRating ? villa.averageRating.toFixed(1) : "0.0"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div className="mt-6 mb-8 w-full">
              <AdCard
                adSlot="3825506421"
                className="w-full h-[100px] sm:h-[110px] md:h-[248px] lg:h-[248px] 
                mx-auto rounded-xl transition-all duration-300"
              />
            </div>

            {/* SEO: Link to more villas in the same city */}
            {villaData.mainCity && (
              <div className="bg-white p-4 rounded-lg border mb-2">
                <Link
                  to={`/villas-in-${villaData.mainCity.toLowerCase()}`}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="font-medium text-gray-900">
                    More villas in {villaData.mainCity}
                  </span>
                  <ArrowRight className="h-4 w-4 text-gray-500" />
                </Link>
              </div>
            )}
          </main>

          {/* Sticky Bottom Bar for Booking */}

          <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-white to-gray-50 shadow-lg border-t border-gray-100 z-50">
            <div className="container mx-auto px-4">
              <div className="flex items-center justify-between py-4 h-[72px]">
                <div className="flex flex-col">
                  <div className="flex items-baseline">
                  </div>
                  {villaData.pricing?.enabled && (
                    <button
                      onClick={() => {
                        setShowPricingPopup(true)
                      }}
                      className="text-[15px] text-red-900 font-semibold hover:text-indigo-700 underline"
                    >
                      View pricing details
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      // Track Book Now click in analytics
                      if (user && villaId) {
                        const analyticsRef = ref(db, `analytics/bookNow/${villaId}`);
                        runTransaction(analyticsRef, (currentCount) => {
                          return (currentCount || 0) + 1;
                        });
                      }
                      setShowWhatsAppForm(true) // Change setIsChatOpen(true)
                    }}
                    className="bg-black text-white hover:bg-gray-800 px-6 py-2.5 rounded-full text-sm font-medium"
                  >
                    Book Now
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Add this floating button just before the closing div of the mobile view */}
          <div className="fixed right-4 bottom-20 z-50">
            <Button
              onClick={() => {
                setShowAvailabilityModal(true)
              }}
              className="bg-black text-white shadow-lg rounded-full px-6 py-3 flex items-center gap-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              Check Availability
            </Button>
          </div>
        </>
      ) : (
        // Keep desktop UI unchanged
        <header className="bg-black text-white p-4 sticky top-0 z-50">
          <div className="container mx-auto px-4 flex items-center justify-between">
            {!isOwnerPreview && (
              <>
                <Link to="/" onClick={handleLogoClick}>
                  <Logo />
                </Link>
                <nav className="flex items-center space-x-4">
                  <Link
                    to="/for-owner"
                    className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out transform hover:scale-105"
                  >
                    For Owners
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out transform hover:scale-105"
                    >
                      Admin Dashboard
                    </Link>
                  )}
                  {isOwner && (
                    <Link
                      to="/owner"
                      className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out transform hover:scale-105"
                    >
                      Your Villa
                    </Link>
                  )}
                  <Link
                    to="/wishlists"
                    className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out transform hover:scale-105"
                  >
                    Wishlists
                  </Link>
                  {user ? (
                    <div className="relative">
                      <button
                        onClick={() => setShowProfileMenu(!showProfileMenu)}
                        className="flex items-center space-x-2 hover:bg-gray-700 px-3 py-2 rounded transition duration-300"
                      >
                        <AvatarCircle
                          src={user.photoURL}
                          name={user.displayName || user.email}
                          className="w-8 h-8"
                          textClassName="text-xs"
                        />
                        <ChevronDownIcon className="h-4 w-4" />
                      </button>
                      {showProfileMenu && (
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 text-gray-700">
                          <div className="px-4 py-2 border-b">
                            <p className="text-sm font-medium">Hi, {user.displayName}</p>
                          </div>
                          <button
                            onClick={goToProfile}
                            className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                          >
                            View Profile
                          </button>
                          {isOwner && (
                            <Link
                              to="/owner"
                              className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                              onClick={() => setShowProfileMenu(false)}
                            >
                              Owner Dashboard
                            </Link>
                          )}
                          {isAdmin && (
                            <button
                              onClick={goToAdminDashboard}
                              className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                            >
                              Admin Dashboard
                            </button>
                          )}
                          <button
                            onClick={handleLogoutClick}
                            className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-red-600"
                          >
                            Logout
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowLoginPopup(true)}
                      className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out transform hover:scale-105"
                    >
                      Login
                    </button>
                  )}
                </nav>
              </>
            )}
          </div>
        </header>
      )}
      {showLoginPopup && (
  <LoginPopup
    isOpen={showLoginPopup}
    onClose={() => setShowLoginPopup(false)}
    onLoginSuccess={() => {
      setShowLoginPopup(false)
    }}
  />
)}
      {isMenuOpen && (
        <div className="md:hidden bg-black text-white p-4">
          <Button
            variant="ghost"
            className="w-full text-left py-2 flex items-center"
            onClick={() => {
              toggleWishlist()
              setIsMenuOpen(false)
            }}
          >
            <Heart className={`h-6 w-6 mr-2 ${isInWishlist ? "fill-current text-red-500" : ""}`} />
            {isInWishlist ? "Remove from Favorites" : "Add to Favorites"}
          </Button>
        </div>
      )}
      {!isMobile && (
        <nav className="bg-gray-200 text-gray-700 p-2">
          <div className="max-w-7xl mx-auto">
            <Link to={isOwnerPreview ? "/owner" : "/"} className="hover:underline">
              Home
            </Link>
            {villaData && (
              <span>
                {" "}
                {">"} {villaData.name}
              </span>
            )}
          </div>
        </nav>
      )}
      {!isMobile && (
        <main className="max-w-7xl mx-auto mt-8 px-4 space-y-8 pb-8">
          {isOwnerPreview && (
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded shadow-sm mb-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm leading-5 font-medium text-blue-800">Preview Mode</h3>
                  <div className="mt-2 text-sm leading-5 text-blue-700">
                    <p>
                      This is how your property appears to potential guests. Use this preview to ensure all information
                      is accurate and appealing.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="flex flex-col lg:flex-row justify-between gap-8 mb-8">
            <div className="lg:w-2/3">
              {/* Visible breadcrumb — helps SEO and UX */}
              <nav className="flex items-center gap-1.5 text-sm text-gray-500 mb-4" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-black">Home</Link>
                <span>/</span>
                <Link to={`/villas-in-${String(villaData?.mainCity || '').toLowerCase().replace(/\s+/g, '-')}`} className="hover:text-black">
                  Villas in {villaData?.mainCity}
                </Link>
                <span>/</span>
                <span className="text-gray-900 font-medium truncate max-w-[200px]">{villaData?.name}</span>
              </nav>

              {/* Desktop gallery — hero left + 2×2 right, square layout */}
              <div className="flex gap-2 mb-8 h-[340px]">
                {/* Large hero image */}
                <div
                  className="flex-shrink-0 w-[55%] h-full cursor-pointer rounded-xl overflow-hidden"
                  onClick={() => { setCurrentIndex(0); setIsGalleryOpen(true) }}
                >
                  <OptimizedImage
                    src={villaData.images[0] || "/placeholder.svg"}
                    alt="Villa main image"
                    preset="heroLarge"
                    eager
                    className="w-full h-full hover:opacity-90 transition-opacity"
                  />
                </div>

                {/* 2 × 2 right grid */}
                <div className="flex-1 h-full grid grid-cols-2 grid-rows-2 gap-2">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="cursor-pointer rounded-xl overflow-hidden"
                      onClick={() => { setCurrentIndex(i); setIsGalleryOpen(true) }}
                    >
                      <OptimizedImage
                        src={villaData.images[i] || "/placeholder.svg"}
                        alt={`Villa image ${i + 1}`}
                        preset="gallery"
                        className="w-full h-full hover:opacity-90 transition-opacity"
                      />
                    </div>
                  ))}

                  {/* +images overlay tile */}
                  <div
                    className="cursor-pointer rounded-xl overflow-hidden relative"
                    onClick={() => { setCurrentIndex(4); setIsGalleryOpen(true) }}
                  >
                    <OptimizedImage
                      src={villaData.images[4] || "/placeholder.svg"}
                      alt="More images"
                      preset="gallery"
                      className="w-full h-full"
                    />
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-xl">
                      <span className="text-white text-xl font-semibold">+{villaData.images.length - 4} images</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-8 bg-white p-4 rounded-lg shadow-md">
                <div className="flex items-start justify-between">
                  <h2 className="text-2xl md:text-3xl font-bold">{villaData.name}</h2>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleWishlist}
                    className={`transition-colors duration-300 ${isInWishlist ? "text-red-500 hover:text-red-600" : "text-gray-500 hover:text-gray-600"}`}
                    aria-label={isInWishlist ? "Remove from favorites" : "Add to favorites"}
                  >
                    <Heart className={`h-6 w-6 transition-all duration-300 ${isInWishlist ? "fill-current" : ""}`} />
                  </Button>
                </div>
                <button
                  onClick={handleAddressClick}
                  className="text-blue-600 hover:text-blue-600 transition-colors text-left"
                >
                  <span className="text-left">
                    <p>{villaData.address}</p>
                  </span>
                </button>
                <div className="flex flex-wrap items-center gap-x-2">
                  <div className="flex items-center gap-1">
                    <span className="font-medium">{villaData.guestCount} Guests</span>
                  </div>

                  {villaData.bedrooms && (
                    <>
                      <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                      <div className="flex items-center gap-1">
                        <span className="font-medium">{villaData.bedrooms} Bedrooms</span>
                      </div>
                    </>
                  )}

                  {villaData.halls && (
                    <>
                      <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                      <div className="flex items-center gap-1">
                        <span className="font-medium">{villaData.halls} Halls</span>
                      </div>
                    </>
                  )}

                  {villaData.kitchens && (
                    <>
                      <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                      <div className="flex items-center gap-1">
                        <span className="font-medium">{villaData.kitchens} Kitchens</span>
                      </div>
                    </>
                  )}

                  {villaData.totalVillaArea && (
                    <>
                      <div className="w-1 h-1 bg-gray-300 rounded-full"></div>
                      <div className="flex items-center gap-1">
                        <span className="font-medium">{villaData.totalVillaArea} m²</span>
                      </div>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`h-5 w-5 ${i < Math.round(villaData.averageRating || 0) ? "text-yellow-400 fill-current" : "text-gray-300"}`}
                      />
                    ))}
                  </div>
                  <a onClick={scrollToReviews} className="text-blue-600 hover:underline cursor-pointer">
                    <span className="text-sm text-gray-600">
                      {villaData.averageRating ? villaData.averageRating.toFixed(1) : "0.0"} (
                      {villaData.reviewCount || 0} reviews)
                    </span>
                  </a>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                <h3 className="text-2xl font-bold mb-4">Facilities</h3>
                <div className="overflow-x-auto -mx-4 scrollbar-hide">
                  <div className="flex pl-4 space-x-2 pb-4">
                    {villaData.facilities
                      ?.filter((facility) => {
                        const facilityId = typeof facility === 'string' ? facility : facility.id;
                        return facilityId !== "stationPickup" && facilityId !== "busStopPickup";
                      })
                      .map((facility, index) => (
                        <FacilityCard
                          key={index}
                          facilityInfo={getFacilityIcon(facility)}
                        />
                      ))}
                  </div>
                </div>
                {villaData.facilities?.length > 5 && (
                  <Button variant="default" onClick={() => setIsFacilitiesPopupOpen(true)} className="mt-4">
                    See More
                  </Button>
                )}
              </div>

              <div className="grid md:flex gap-4 mb-8">
                <div className="flex-1 bg-white p-6 rounded-lg shadow-md h-auto md:h-[225px]">
                  <h3 className="text-2xl font-bold mb-4">About This Place</h3>
                  <p className="text-gray-600 line-clamp-3 mb-4">{villaData.description}</p>
                  <Button ref={aboutButtonRef} variant="default" onClick={() => setIsAboutPopupOpen(true)}>
                    Read More
                  </Button>
                </div>
                <div
                  className="w-full md:w-[300px] bg-black text-white p-6 rounded-lg shadow-md relative h-[200px] md:h-[225px]"
                  {...handlers}
                >
                  <div className="flex flex-col md:block">
                    <h3 className="text-2xl font-bold mb-4 text-left">Management</h3>
                    <div
                      className="flex items-center md:flex-col md:items-center justify-start"
                      onTouchStart={() => setIsPaused(true)}
                      onTouchEnd={() => setIsPaused(false)}
                    >
                      <div className="relative w-24 md:w-20 h-24 md:h-20 mb-0 md:mb-2">
                        {activeManager?.image ? (
                          <img
                            src={activeManager.image || "/placeholder.svg"}
                            alt={activeManager.name || "Management"}
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full rounded-full flex items-center justify-center bg-gray-100">
                            <img src={sadFaceIcon} alt="Management details not shared" className="w-12 h-12 opacity-80" />
                          </div>
                        )}
                        <div
                          className="absolute inset-0 bg-gradient-to-r from-red-500 to-transparent rounded-full"
                          style={{ clipPath: "inset(0 100% 0 0)" }}
                        />
                      </div>
                      <div className="ml-10 md:ml-0 text-center md:text-center">
                        <p className="font-bold text-lg">{activeManager?.name || "Management details not shared"}</p>
                        {managementMembers.length > 1 && (
                          <div className="flex justify-start md:justify-center gap-2 mt-2">
                            {managementMembers.map((_, index) => (
                              <div
                                key={index}
                                className={`w-2 h-2 rounded-full ${index === currentManagerIndex ? "bg-white" : "bg-gray-600"}`}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                <h3 className="text-2xl font-bold mb-4">About The Location</h3>
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="aspect-square w-full rounded-lg overflow-hidden">
                    <div className="relative h-full w-full" {...locationSwipeHandlers}>
                      {villaData.locationImages && villaData.locationImages.length > 0 ? (
                        <div className="relative h-full">
                          <div
                            className="absolute inset-0 transition-transform duration-300"
                            style={{ transform: `translateX(-${currentLocationImageIndex * 100}%)` }}
                          >
                            {villaData.locationImages.map((image, index) => (
                              <img
                                key={index}
                                src={image || "/placeholder.svg"}
                                alt={`Location ${index + 1}`}
                                className="absolute inset-0 w-full h-full object-cover"
                                style={{ left: `${index * 100}%`, width: "100%" }}
                              />
                            ))}
                          </div>
                          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
                            {villaData.locationImages.map((_, index) => (
                              <button
                                key={index}
                                className={`w-2 h-2 rounded-full transition-colors ${index === currentLocationImageIndex ? "bg-white" : "bg-white/50 hover:bg-white/75"}`}
                                onClick={() => {
                                  setCurrentLocationImageIndex(index)
                                  setIsLocationImagesPaused(true)
                                  setTimeout(() => setIsLocationImagesPaused(false), 5000)
                                }}
                              />
                            ))}
                          </div>
                          {villaData.locationImages.length > 1 && (
                            <>
                              <button
                                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 p-2 rounded-full text-white hidden md:block hover:bg-black/70 transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setCurrentLocationImageIndex(
                                    (prev) =>
                                      (prev - 1 + villaData.locationImages.length) % villaData.locationImages.length,
                                  )
                                  setIsLocationImagesPaused(true)
                                  setTimeout(() => setIsLocationImagesPaused(false), 5000)
                                }}
                              >
                                <ChevronLeft className="h-6 w-6" />
                              </button>
                              <button
                                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 p-2 rounded-full text-white hidden md:block hover:bg-black/70 transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setCurrentLocationImageIndex((prev) => (prev + 1) % villaData.locationImages.length)
                                  setIsLocationImagesPaused(true)
                                  setTimeout(() => setIsLocationImagesPaused(false), 5000)
                                }}
                              >
                                <ChevronRight className="h-6 w-6" />
                              </button>
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-full bg-gray-100 rounded-lg">
                          <p className="text-gray-500">No location images available</p>
                        </div>
                      )}
                      {villaData.location?.lat && villaData.location?.lng && (
                        <div
                          className="absolute inset-0 transition-opacity duration-300"
                          style={{
                            opacity: currentLocationImageIndex === villaData.locationImages?.length ? 1 : 0,
                            pointerEvents:
                              currentLocationImageIndex === villaData.locationImages?.length ? "auto" : "none",
                          }}
                        >
                          <GoogleMap
                            villaName={villaData.name}
                            address={villaData.address}
                            latitude={villaData.location?.lat}
                            longitude={villaData.location?.lng}
                            googleMapsLink={villaData.googleMapsLink}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 md:mt-0">
                    <ul className="space-y-2">
                      {villaData.locationHighlights?.map((highlight, index) => (
                        <li key={index} className="flex">
                          <span className="mr-2">•</span>
                          <span className="flex-1">{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                <h3 className="text-2xl font-bold mb-4">Nearest Transportation</h3>
                <div className="grid grid-cols-2 gap-18">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-gray-100 rounded-lg">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        fill="currentColor"
                        class="bi bi-train-front"
                        viewBox="0 0 16 16"
                      >
                        <path d="M5.621 1.485c1.815-.454 2.943-.454 4.758 0 .784.196 1.743.673 2.527 1.119.688.39 1.094 1.148 1.094 1.979V13.5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 13.5V4.583c0-.831.406-1.588 1.094-1.98.784-.445 1.744-.922 2.527-1.118m5-.97C8.647.02 7.353.02 5.38.515c-.924.23-1.982.766-2.78 1.22C1.566 2.322 1 3.432 1 4.582V13.5A2.5 2.5 0 0 0 3.5 16h9a2.5 2.5 0 0 0 2.5-2.5V4.583c0-1.15-.565-2.26-1.6-2.849-.797-.453-1.855-.988-2.779-1.22ZM5 13a1 1 0 1 1-2 0 1 1 0 0 1 2 0m0 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0m7 1a1 1 0 1 0-1-1 1 1 0 1 0-2 0 1 1 0 0 0 2 0 1 1 0 0 0 1 1M4.5 5a.5.5 0 0 0-.5.5v2a.5.5 0 0 0 .5.5h3V5zm4 0v3h3a.5.5 0 0 0 .5-.5v-2a.5.5 0 0 0-.5-.5zM3 5.5A1.5 1.5 0 0 1 4.5 4h7A1.5 1.5 0 0 1 13 5.5v2A1.5 1.5 0 0 1 11.5 9h-7A1.5 1.5 0 0 1 3 7.5zM6.5 2a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Train Station</p>
                      <p className="font-semibold">{villaData.trainDistance} km</p>
                      {!!villaData.stationPickup && (
                        <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M20 6L9 17L4 12"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          Pickup/Drop Available
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-gray-100 rounded-lg">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        fill="currentColor"
                        class="bi bi-bus-front"
                        viewBox="0 0 16 16"
                      >
                        <path d="M5 11a1 1 0 1 1-2 0 1 1 0 0 1 2 0m8 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0m-6-1a1 1 0 1 0 0 2h2a1 1 0 1 0 0-2zm1-6c-1.876 0-3.426.109-4.552.226A.5.5 0 0 0 3 4.723v3.554a.5.5 0 0 0 .448.497C4.574 8.891 6.124 9 8 9s3.426-.109 4.552-.226A.5.5 0 0 0 13 8.277V4.723a.5.5 0 0 0-.448-.497A44 44 0 0 0 8 4m0-1c-1.837 0-3.353.107-4.448.22a.5.5 0 1 1-.104-.994A44 44 0 0 1 8 2c1.876 0 3.426.109 4.552.226a.5.5 0 1 1-.104.994A43 43 0 0 0 8 3" />
                        <path d="M15 8a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1V2.64c0-1.188-.845-2.232-2.064-2.372A44 44 0 0 0 8 0C5.9 0 4.208.136 3.064.268 1.845.408 1 1.452 1 2.64V4a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1v3.5c0 .818.393 1.544 1 2v2a.5.5 0 0 0 .5.5h2a.5.5 0 0 0 .5-.5V14h6v1.5a.5.5 0 0 0 .5.5h2a.5.5 0 0 0 .5-.5v-2c.607-.456 1-1.182 1-2zM8 1c2.056 0 3.71.134 4.822.261.676.078 1.178.66 1.178 1.379v8.86a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 11.5V2.64c0-.72.502-1.301 1.178-1.379A43 43 0 0 1 8 1" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Bus Stop</p>
                      <p className="font-semibold">{villaData.busDistance} km</p>
                      {!!villaData.busStopPickup && (
                        <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M20 6L9 17L4 12"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          Pickup/Drop Available
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                <h3 className="text-2xl font-bold mb-4">Check-in & Check-out</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-gray-100 rounded-lg">
                      <Clock className="h-6 w-6 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Check-in Time</p>
                      <p className="font-semibold">{villaData.checkInTime || "3:00 PM"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-gray-100 rounded-lg">
                      <Clock className="h-6 w-6 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Check-out Time</p>
                      <p className="font-semibold">{villaData.checkOutTime || "11:00 AM"}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                <h3 className="text-2xl font-bold mb-4">Meals</h3>

                <p className="text-gray-600 mb-6">
                  {villaData.meals?.description ||
                    "We provide healthy home-cooked meals including breakfast, lunch, and dinner"}
                </p>

                <div className="flex items-center gap-6 mb-6">
                  {villaData.meals?.types?.includes("veg") && (
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6">
                        <svg viewBox="0 0 24 24">
                          <rect width="20" height="20" x="2" y="2" fill="none" stroke="green" strokeWidth="2" />
                          <circle cx="12" cy="12" r="6" fill="green" />
                        </svg>
                      </div>
                      <span>Vegetarian</span>
                    </div>
                  )}

                  {villaData.meals?.types?.includes("nonveg") && (
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6">
                        <svg viewBox="0 0 24 24">
                          <rect width="20" height="20" x="2" y="2" fill="none" stroke="red" strokeWidth="2" />
                          <circle cx="12" cy="12" r="6" fill="red" />
                        </svg>
                      </div>
                      <span>Non-Vegetarian</span>
                    </div>
                  )}

                  {villaData.meals?.types?.includes("vegan") && (
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6">
                        <svg viewBox="0 0 24 24">
                          <rect width="20" height="20" x="2" y="2" fill="none" stroke="darkgreen" strokeWidth="2" />
                          <path d="M12 6l-6 12h12z" fill="darkgreen" />
                        </svg>
                      </div>
                      <span>Vegan</span>
                    </div>
                  )}
                </div>

                <div className="overflow-x-auto -mx-4 scrollbar-hide">
                  <div className="flex gap-4 px-4 pb-4">
                    {villaData.meals?.images?.map((image, index) => (
                      <div
                        key={index}
                        className="flex-shrink-0 w-[180px] h-[240px] rounded-lg overflow-hidden shadow-md cursor-pointer"
                        onClick={() =>
                          navigate("/meal-gallery", {
                            state: {
                              images: villaData.meals.images,
                              mealDescription: villaData.meals?.description || "Meal Image",
                            },
                          })
                        }
                      >
                        <img
                          src={image || "/placeholder.svg"}
                          alt={`Meal ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Architectural Uniqueness */}
              {villaData.architecturalUniqueness && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <h3 className="text-2xl font-bold mb-4">Architectural Uniqueness</h3>
                  <p className="text-gray-600">{villaData.architecturalUniqueness}</p>
                </div>
              )}

              {/* Weather (Desktop) */}
              <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                <h3 className="text-2xl font-bold mb-4">Weather at {villaData.name}</h3>
                {isWeatherLoading && <p className="text-gray-500 text-sm">Loading weather...</p>}
                {weatherError && <p className="text-red-500 text-sm">{weatherError}</p>}
                {!isWeatherLoading && !weatherError && weather && (
                  <div>
                    {weather.current && (
                      <div className="flex items-center gap-10 mb-6">
                        <div className="flex items-center gap-4">
                          <img
                            src={`https://openweathermap.org/img/wn/${weather.current.weather?.[0]?.icon || "01d"}@2x.png`}
                            alt="Current"
                            className="w-24 h-24"
                          />
                          <div>
                            <p className="text-4xl font-semibold leading-tight">
                              {Math.round(weather.current.temp)}°C
                            </p>
                            <p className="text-sm text-gray-600 capitalize">
                              {weather.current.weather?.[0]?.description || ""}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-6 text-sm text-gray-600">
                          <div>Feels like <span className="font-medium">{Math.round(weather.current.feels_like)}°C</span></div>
                          <div>Humidity <span className="font-medium">{weather.current.humidity}%</span></div>
                          <div>Wind <span className="font-medium">{Math.round(weather.current.wind_speed)} m/s</span></div>
                        </div>
                      </div>
                    )}
                    {weather.daily && weather.daily.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
                        {weather.daily.slice(0, 8).map((d, i) => {
                          const date = new Date(d.dt * 1000)
                          const label = i === 0 ? "Today" : date.toLocaleDateString(undefined, { weekday: "short" })
                          return (
                            <div key={d.dt || i} className="bg-gray-50 rounded-lg p-4 text-center border">
                              <p className="text-sm font-medium mb-1">{label}</p>
                              <img
                                src={`https://openweathermap.org/img/wn/${d.weather?.[0]?.icon || "01d"}@2x.png`}
                                alt={d.weather?.[0]?.main || ""}
                                className="w-14 h-14 mx-auto -my-1"
                              />
                              <p className="text-xs capitalize truncate">{d.weather?.[0]?.main || ""}</p>
                              <p className="text-sm font-semibold mt-1">
                                {Math.round(d.temp?.max)}° / {Math.round(d.temp?.min)}°
                              </p>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                <h3 className="text-2xl font-bold mb-4">Villa Rules</h3>
                <ul className="space-y-2">
                  {villaData.rules &&
                    villaData.rules.map((rule, index) => (
                      <li key={index} className="flex">
                        <span className="mr-2">•</span>
                        <span className="flex-1">{rule}</span>
                      </li>
                    ))}
                </ul>
              </div>
              

              {/* Spaces Section */}
              {villaData.spaces && villaData.spaces.length > 0 && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <h3 className="text-2xl font-bold mb-4">Interior & Amenities</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {villaData.spaces.map((space, index) => (
                      <div key={index} className="relative rounded-lg overflow-hidden border shadow-sm">
                        <div className="relative h-[240px]">
                          <img
                            src={space.image || "/placeholder.svg"}
                            alt={space.name}
                            className="w-full h-full object-cover rounded-lg"
                          />
                          {space.topLabel && (
                            <div className="absolute top-3 right-3 bg-white px-3 py-1 rounded-md text-sm font-medium shadow-sm">
                              {space.topLabel}
                            </div>
                          )}
                          {space.name && (
                            <div className="absolute bottom-3 left-3 bg-white px-3 py-1 rounded-md text-sm font-bold shadow-sm">
                              {space.name}
                            </div>
                          )}
                        </div>
                        <div className="p-4">
                          <ul className="space-y-2">
                            {space.description?.map((desc, descIndex) => (
                              <li key={descIndex} className="flex items-start">
                                <span className="mr-2 text-lg">•</span>
                                <span className="flex-1 text-gray-700">{desc}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Cancellation Policy - New Section */}
              {villaData.cancellationPolicy && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <h3 className="text-2xl font-bold mb-4">Cancellation Policy</h3>
                  <div className="text-gray-600 whitespace-pre-line">{villaData.cancellationPolicy}</div>
                </div>
              )}

              {/* Featured Villa Shorts */}
              {villaData.youtubeShorts && villaData.youtubeShorts.length > 0 && (
                <FeaturedShorts shorts={villaData.youtubeShorts} />
              )}

              <div ref={reviewsRef} className="bg-white p-6 rounded-lg shadow-md mb-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold">Ratings and reviews</h3>
                </div>
                <div className="flex items-center gap-8 mb-6">
                  <div className="text-center">
                    <div className="text-4xl font-bold" style={{ color: ratingColor }}>
                      {averageRating.toFixed(1)}
                    </div>
                    <div className="text-sm font-semibold">
                      {averageRating >= 4.5
                        ? "EXCELLENT"
                        : averageRating >= 4.0
                          ? "VERY GOOD"
                          : averageRating >= 3.0
                            ? "GOOD"
                            : averageRating >= 2.0
                              ? "FAIR"
                              : "POOR"}
                    </div>
                    <div className="text-sm text-gray-500">{reviews.length} ratings</div>
                  </div>
                  <div className="flex-1">
                    {[5, 4, 3, 2, 1].map((rating) => {
                      const count = reviews.filter((r) => r.rating === rating).length
                      const percentage = reviews.length > 0 ? (count / reviews.length) * 100 : 0
                      const getRatingColor = (rating) => {
                        switch (rating) {
                          case 5:
                            return "bg-green-500"
                          case 4:
                            return "bg-green-400"
                          case 3:
                            return "bg-yellow-400"
                          case 2:
                            return "bg-orange-400"
                          case 1:
                            return "bg-red-400"
                          default:
                            return "bg-gray-200"
                        }
                      }
                      return (
                        <div key={rating} className="flex items-center gap-2 mb-1">
                          <span className="w-3">{rating}</span>
                          <Star className={`h-4 w-4 ${count > 0 ? "text-yellow-400" : "text-gray-400"}`} />
                          <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-300 ${getRatingColor(rating)}`}
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                          <span className="w-12 text-sm text-gray-500">{percentage.toFixed(0)}%</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
                <div className="space-y-6">
                  {reviews.slice(0, showAllReviews ? undefined : 4).map((review) => (
                    <div
                      key={review.id}
                      className="border-b pb-4 cursor-pointer hover:bg-gray-50 transition-colors rounded-lg"
                      onClick={() => setSelectedReview(review)}
                    >
                      <div className="flex items-center mb-2">
                        <img
                          src={userPhotos[review.userId] || review.userPhoto || "/placeholder.svg"}
                          alt={userData[review.userId]?.displayName || review.userName}
                          className="w-10 h-10 rounded-full mr-2"
                        />
                        <div>
                          <p className="font-bold">{userData[review.userId]?.displayName || review.userName}</p>
                          <div className="flex items-center gap-2">
                            <div className="flex">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`h-4 w-4 ${i < review.rating ? "text-yellow-400 fill-current" : "text-gray-300"}`}
                                />
                              ))}
                            </div>
                            <span className="text-sm text-gray-500">{getTimeDifference(review.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-700 mb-2">{review.comment}</p>
                      {user && villaData.ownerEmail === user.email && !review.showReplyForm && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const updatedReviews = reviews.map((r) =>
                              r.id === review.id ? { ...r, showReplyForm: true } : r,
                            )
                            setReviews(updatedReviews)
                          }}
                          className="text-blue-600"
                        >
                          Reply
                        </Button>
                      )}
                      {review.replies &&
                        Object.entries(review.replies).map(([replyId, reply]) => (
                          <div key={replyId} className="ml-8 mt-2 bg-gray-50 p-3 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <img
                                src={reply.userPhoto || "/placeholder.svg"}
                                alt={reply.userName}
                                className="w-8 h-8 rounded-full"
                              />
                              <div>
                                <p className="font-semibold">
                                  {reply.userName}
                                  {reply.userId === villaData.owner.id && " (Owner)"}
                                </p>
                                <p className="text-sm text-gray-500">{getTimeDifference(reply.createdAt)}</p>
                              </div>
                            </div>
                            <p className="text-gray-700">{reply.text}</p>
                          </div>
                        ))}
                      {user && review.showReplyForm && (
                        <div className="mt-3 ml-8">
                          <Textarea
                            placeholder="Write your reply..."
                            className="w-full mb-2"
                            value={review.replyText || ""}
                            onChange={(e) => {
                              const updatedReviews = reviews.map((r) =>
                                r.id === review.id ? { ...r, replyText: e.target.value } : r,
                              )
                              setReviews(updatedReviews)
                            }}
                          />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleReplySubmit(review.id, review.replyText)}>
                              Submit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const updatedReviews = reviews.map((r) =>
                                  r.id === review.id ? { ...r, showReplyForm: false, replyText: "" } : r,
                                )
                                setReviews(updatedReviews)
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {reviews.length > 4 && (
                  <div className="mt-6 text-center">
                    <Button variant="outline" onClick={() => {
                      if (!user) {
                        alert("Please login to see all reviews")
                        return
                      } setShowAllReviews(!showAllReviews)
                    }}>
                      {showAllReviews ? "Show Less" : `Show All ${reviews.length} Reviews`}
                    </Button>
                  </div>
                )}
                <div className="mt-6 text-center">
                  <Button
                    ref={reviewButtonRef}
                    onClick={() => {
                      if (!user) {
                        alert("You must be logged in to write a review.")
                        return
                      }
                      if (hasUserReviewed) {
                        alert("You have already submitted a review for this villa.")
                        return
                      }
                      setIsReviewPopupOpen(true)
                    }}
                    disabled={hasUserReviewed}
                    className={hasUserReviewed ? "opacity-50 cursor-not-allowed" : ""}
                  >
                    {hasUserReviewed ? "Already Reviewed" : "Write a Review"}
                  </Button>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                <h3 className="text-2xl font-bold mb-4">Location</h3>
                <div className="space-y-4">
                  <div className="h-[400px] rounded-lg overflow-hidden relative">
                    {user ? (
                      villaData.location?.lat && villaData.location?.lng ? (
                        <GoogleMap
                          villaName={villaData.name}
                          address={villaData.address}
                          latitude={villaData.location?.lat}
                          longitude={villaData.location?.lng}
                          googleMapsLink={villaData.googleMapsLink}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-100">
                          <p className="text-gray-500">Location map not available</p>
                        </div>
                      )
                    ) : (
                      <div className="relative w-full h-full">
                        <div className="absolute inset-0 backdrop-blur-[8px] bg-gray-100/50 flex items-center justify-center z-10">
                          <div className="text-center">
                            <p className="text-lg font-semibold mb-2">Login to see location</p>
                            <button
                              onClick={() => setShowLoginPopup(true)}
                              className="bg-black text-white px-4 py-2 rounded-full text-sm hover:bg-gray-800"
                            >
                              Login
                            </button>
                          </div>
                        </div>
                        <div className="w-full h-full bg-gray-200"></div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-center mt-4">
                    {user ? (
                      <a
                        href={
                          villaData.googleMapsLink ||
                          `https://www.google.com/maps/search/${encodeURIComponent(villaData.name)}/@${villaData.location?.lat},${villaData.location?.lng},17z`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-full hover:bg-gray-800 transition-colors"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        Open in Google Maps
                      </a>
                    ) : (
                      <button
                        onClick={() => setShowLoginPopup(true)}
                        className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-full hover:bg-gray-800 transition-colors"
                      >
                        Login to view location
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:w-1/3">
              <div
                className="bg-white p-8 rounded-2xl shadow-lg lg:sticky border border-gray-100"
                style={{ top: "80px", maxHeight: "calc(100vh - 100px)", overflowY: "auto", zIndex: 10 }}
                ref={enquiryRef}
              >
                {/* Price Section */}
                <div>
                  <h3 className="text-[#FF4747] text-xl font-bold">Starting From</h3>
                  <p className="text-[23px] font-semibold text-[#1E1E1E]">
                    ₹{villaData.rate}
                    <span className="text-base font-normal">/Night</span>
                  </p>
                  {villaData.pricing?.enabled && (
                    <button onClick={() => {
                      setShowPricingPopup(true)
                    }}
                      className="text-sm text-blue-600 underline">
                      View detailed pricing
                    </button>
                  )}
                </div>

                {/* Booking Actions */}
                <div className="space-y-4 mb-8">
                  <Button
                    onClick={() => {
                      // Track Book Now click in analytics
                      if (user && villaId) {
                        const analyticsRef = ref(db, `analytics/bookNow/${villaId}`);
                        runTransaction(analyticsRef, (currentCount) => {
                          return (currentCount || 0) + 1;
                        });
                      }
                      setShowWhatsAppForm(true);
                    }}
                    className="w-full bg-black hover:bg-gray-900 text-white py-4 rounded-xl font-medium transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
                  >
                    Book Now
                  </Button>
                  <Button
                    onClick={() => {
                      setShowAvailabilityModal(true)
                    }}
                    className="w-full bg-black hover:bg-gray-900 text-white py-4 rounded-xl font-medium transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
                  >
                    <Calendar className="h-5 w-5" />
                    Check Availability
                  </Button>

                  <div className="flex gap-3">
                    {/*
                    <ChatButton
                      villaId={villaId}
                      ownerId={villaData.owner.id}
                      className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-4 rounded-xl font-medium transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
                      onClick={handleChatClick}
                    />
                    */}
                  </div>
                </div>

                {/* Owner Section */}
                <div className="border-t border-gray-100 pt-6 mb-6">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <img
                        src={villaData.owner.image || "/placeholder.svg"}
                        alt={villaData.owner.name}
                        className="w-14 h-14 rounded-full object-cover ring-2 ring-gray-100"
                      />
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white"></div>
                    </div>
                    <div>
                      <p className="text-gray-600 text-sm">Owned By</p>
                      <p className="font-semibold text-lg">{villaData.owner.name}</p>
                    </div>
                  </div>
                </div>

                {/* YouTube Section */}
                {villaData.youtubeLink && (
                  <div className="border-t border-gray-100 pt-6">
                    <button
                      className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white py-4 rounded-xl font-medium transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
                      onClick={() => window.open(villaData.youtubeLink, "_blank")}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                      </svg>
                      Watch Villa Tour
                    </button>
                  </div>
                )}

                {/* Quick Info */}
                <div className="border-t border-gray-100 pt-6 mt-6">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Users2 className="h-4 w-4 text-gray-500" />
                      <span>{villaData.guestCount} guests</span>
                    </div>

                    {villaData.bedrooms && (
                      <div className="flex items-center gap-2">
                        <BedDouble className="h-4 w-4 text-gray-500" />
                        <span>{villaData.bedrooms} bedrooms</span>
                      </div>
                    )}

                    {villaData.halls && (
                      <div className="flex items-center gap-2">
                        <Home className="h-4 w-4 text-gray-500" />
                        <span>{villaData.halls} halls</span>
                      </div>
                    )}

                    {villaData.kitchens && (
                      <div className="flex items-center gap-2">
                        <ChefHat className="h-4 w-4 text-gray-500" />
                        <span>{villaData.kitchens} kitchens</span>
                      </div>
                    )}

                    {villaData.totalVillaArea && (
                      <div className="flex items-center gap-2">
                        <Ruler className="h-4 w-4 text-gray-500" />
                        <span>{villaData.totalVillaArea} m²</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                      <span>
                        {averageRating.toFixed(1)} ({reviews.length})
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {!isOwnerPreview && recommendedVillas.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow-md mb-8">
              <h3 className="text-2xl font-bold mb-4">Recommended Villas</h3>
              <div className="relative" {...recommendedSwipeHandlers}>
                <div className="overflow-hidden">
                  <div
                    className="flex md:grid md:grid-cols-4 md:gap-4 transition-transform duration-300 ease-in-out"
                    style={{ transform: `translateX(-${currentRecommendedIndex * (isMobile ? 100 : 25)}%)` }}
                  >
                    {recommendedVillas.map((villa, index) => (
                      <div key={villa.id} className="w-full md:w-auto flex-shrink-0 px-2 md:px-0">
                        <Link to={`/villa/${villa.id}`} className="block bg-white rounded-lg overflow-hidden">
                          <img
                            src={villa.thumbnail || villa.images[0] || "/placeholder.svg"}
                            alt={villa.name}
                            className="w-full h-48 object-cover rounded-t-lg"
                          />
                          <div className="p-4">
                            <h4 className="font-semibold text-lg">{villa.name}</h4>
                            <p className="text-gray-600">{villa.mainCity}</p>
                            <p className="font-bold">₹{villa.rate} onwords</p>
                          </div>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
                <button
                  className="absolute top-1/2 left-2 transform -translate-y-1/2 bg-white rounded-full p-2 shadow-md hidden md:block disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleSwipeRight}
                  disabled={currentRecommendedIndex === 0}
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  className="absolute top-1/2 right-2 transform -translate-y-1/2 bg-white rounded-full p-2 shadow-md hidden md:block disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleSwipeLeft}
                  disabled={currentRecommendedIndex >= recommendedVillas.length - 4}
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </div>
              <div className="mt-6 mb-8 w-full">
                <AdCard
                  adSlot="3825506421"
                  className="w-full h-[100px] sm:h-[110px] md:h-[248px] lg:h-[248px] 
                  mx-auto rounded-xl transition-all duration-300"
                />
              </div>
            </div>
          )}
        </main>
      )}
      {!isMobile && <Footer />}
      <Popup
        isOpen={isAboutPopupOpen}
        onClose={() => setIsAboutPopupOpen(false)}
        title="About This Place"
        buttonRef={aboutButtonRef}
      >
        <div className="p-4">
          {villaData.description.split('\n').map((paragraph, index) => (
            <p
              key={index}
              className={`text-gray-700 leading-relaxed ${index !== 0 ? 'mt-4' : ''
                }`}
            >
              {paragraph.trim().replace(/\s+/g, ' ')}
            </p>
          ))}
        </div>
      </Popup>
      <Popup
        isOpen={isFacilitiesPopupOpen}
        onClose={() => setIsFacilitiesPopupOpen(false)}
        title="Facilities"
        buttonRef={facilitiesButtonRef}
      >
        <div className="p-4">
          {/* Add a legend for paid facilities */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg flex items-center gap-2">
            <div className="bg-amber-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold">
              ₹
            </div>
            <span className="text-sm text-gray-600">
              These facilities are paid and not included in the package
            </span>
          </div>

          {/* Facilities grid */}
          <div className="grid grid-cols-2 gap-6">
            {villaData.facilities
              .filter((facility) => facility !== "stationPickup" && facility !== "busStopPickup")
              .map((facility, index) => {
                const facilityInfo = getFacilityIcon(facility);
                return (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="relative">
                      {facilityInfo.isPaid && (
                        <div className="absolute -top-1 -right-1 bg-amber-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold shadow-sm border border-white">
                          ₹
                        </div>
                      )}
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        {facilityInfo.icon}
                      </div>
                    </div>
                    <span className="text-[14px] text-gray-700">{facilityInfo.label}</span>
                  </div>
                );
              })}
          </div>
        </div>
      </Popup>
      <Popup
        isOpen={isReviewPopupOpen}
        onClose={() => setIsReviewPopupOpen(false)}
        title="Write a Review"
        buttonRef={reviewButtonRef}
      >
        {hasUserReviewed ? (
          <div className="p-4 text-center">
            <p className="text-gray-600">You have already submitted a review for this villa.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label htmlFor="rating">Rating</Label>
              <div className="flex items-center mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`h-8 w-8 cursor-pointer ${star <= newReview.rating ? "text-yellow-400 fill-current" : "text-gray-300"}`}
                    onClick={() => setNewReview({ ...newReview, rating: star })}
                  />
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="comment">Your Review</Label>
              <Textarea
                id="comment"
                value={newReview.comment}
                onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                rows={4}
                className="mt-2"
                placeholder="Write your review here..."
              />
            </div>
            <Button onClick={handleReviewSubmit} disabled={!newReview.rating || !newReview.comment.trim()}>
              Submit Review
            </Button>
          </div>
        )}
      </Popup>
      <ImageGallery
        images={villaData.images}
        isOpen={isGalleryOpen}
        onClose={() => setIsGalleryOpen(false)}
        initialIndex={currentIndex}
      />
      {showStickyBar && !isMobile && (
        <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t p-4 flex justify-between items-center w-full md:hidden z-50">
          <div>
            <p className="text-xl font-bold">
              From ₹{villaData.rate}
              <span className="text-base font-normal text-gray-600"></span>
            </p>
          </div>
          <button
            onClick={scrollToEnquiry}
            className="bg-black text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors"
          >
            Book Now
          </button>
        </div>
      )}
      {showLogoutConfirmation && (
        <LogoutConfirmationPopup onConfirm={handleLogoutConfirm} onCancel={handleLogoutCancel} />
      )}
      <Routes>
        <Route
          path="/photos"
          element={
            <MobileImageGallery
              onImageClick={(index) => {
                setCurrentIndex(index)
                setIsGalleryOpen(true)
                navigate(-1)
              }}
            />
          }
        />
      </Routes>
      {showAvailabilityModal && (
        <CheckAvailabilityModal
          isOpen={showAvailabilityModal}
          onClose={() => setShowAvailabilityModal(false)}
          villaId={villaId}
          villaName={villaData.name}
          onDateSelect={handleDateSelect}
        />
      )}
      {/* Add this with your other Popup components */}
      <ReviewDetailPopup
        review={selectedReview}
        isOpen={!!selectedReview}
        onClose={() => setSelectedReview(null)}
        villaData={villaData}
      />
      <Popup isOpen={isRulesPopupOpen} onClose={() => setIsRulesPopupOpen(false)} title="Villa Rules">
        <div className="space-y-4 p-4">
          <ul className="space-y-4">
            {villaData.rules?.map((rule, index) => (
              <li key={index} className="flex items-start">
                <span className="mr-2 text-lg font-bold">•</span>
                <span className="flex-1 text-gray-700">{rule}</span>
              </li>
            ))}
          </ul>
        </div>
      </Popup>
      <PricingDetailPopup
        isOpen={showPricingPopup}
        onClose={() => setShowPricingPopup(false)}
        pricing={villaData.pricing}
      />

      {/* WhatsApp Enquiry Form */}
      {showWhatsAppForm && (
        <WhatsAppEnquiryForm
          isOpen={showWhatsAppForm}
          onClose={() => setShowWhatsAppForm(false)}
          villaName={villaData.name}
          whatsappNumber={villaData.whatsappNumber || "918668737989"} // Replace with actual WhatsApp number
          checkInDate={checkInDate}
          checkOutDate={checkOutDate}
          guestCount={guestCount}
          villaId={villaId}
          onSubmit={(data) => {
            console.log("WhatsApp enquiry submitted:", data)
            // You can add additional handling here if needed
          }}
        />
      )}
    </div>
  )
}

const ReviewDetailPopup = ({ review, isOpen, onClose, villaData }) => {
  // Add state for user data
  const [reviewUserData, setReviewUserData] = useState({})
  const [replyUserData, setReplyUserData] = useState({})

  // Add useEffect to fetch user data
  useEffect(() => {
    if (!review) return

    const usersRef = ref(db, "users")
    const unsubscribe = onValue(usersRef, (snapshot) => {
      const users = snapshot.val()
      if (users) {
        // Get reviewer data
        if (users[review.userId]) {
          setReviewUserData(users[review.userId])
        }

        // Get reply authors data
        if (review.replies) {
          const replyUsers = {}
          Object.values(review.replies).forEach((reply) => {
            if (users[reply.userId]) {
              replyUsers[reply.userId] = users[reply.userId]
            }
          })
          setReplyUserData(replyUsers)
        }
      }
    })

    return () => unsubscribe()
  }, [review])

  if (!review) return null

  return (
    <Popup isOpen={isOpen} onClose={onClose} title="Review Details">
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <img
            src={reviewUserData?.photoURL || review.userPhoto || "/placeholder.svg"}
            alt={reviewUserData?.displayName || review.userName}
            className="w-12 h-12 rounded-full object-cover"
          />
          <div>
            <p className="font-bold text-lg">{reviewUserData?.displayName || review.userName}</p>
            <div className="flex items-center gap-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`h-4 w-4 ${i < review.rating ? "text-yellow-400 fill-current" : "text-gray-300"}`}
                  />
                ))}
              </div>
              <span className="text-sm text-gray-500">{getTimeDifference(review.createdAt)}</span>
            </div>
          </div>
        </div>

        <div className="text-gray-700 text-base mt-4">{review.comment}</div>

        {review.replies && Object.entries(review.replies).length > 0 && (
          <div className="mt-6 space-y-4">
            <h4 className="font-semibold text-lg">Replies</h4>
            {Object.entries(review.replies).map(([replyId, reply]) => (
              <div key={replyId} className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <img
                    src={replyUserData[reply.userId]?.photoURL || reply.userPhoto || "/placeholder.svg"}
                    alt={replyUserData[reply.userId]?.displayName || reply.userName}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold">
                      {replyUserData[reply.userId]?.displayName || reply.userName}
                      {reply.userId === villaData?.owner?.id && " (Owner)"}
                    </p>
                    <p className="text-sm text-gray-500">{getTimeDifference(reply.createdAt)}</p>
                  </div>
                </div>
                <p className="text-gray-700 ml-10">{reply.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </Popup>
  )
}

const PricingDetailPopup = ({ isOpen, onClose, pricing }) => {
  return (
    <Popup isOpen={isOpen} onClose={onClose} title="Detailed Pricing">
      <div className="p-2 space-y-2">
        <div className="divide-y divide-gray-100">
          {pricing?.categories?.map((category, index) => (
            <div key={index} className="py-4 first:pt-0 last:pb-0">
              <div className="flex justify-between items-center gap-2">
                <div className="flex-1">
                  <h4 className="text-[16px] font-medium text-gray-900">{category.heading}</h4>
                </div>
                <div className="text-right">
                  <span className="text-[16px] font-semibold text-gray-900">
                    ₹{category.price.toLocaleString()}
                  </span>
                  {category.unit && (
                    <span className="text-[16px] text-gray-500 ml-1">/{category.unit}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {pricing?.note && (
          <div className="mt-2 bg-gray-50 rounded-lg p-4">
            <h4 className="text-[16px] font-medium text-gray-900 mb-2">Note:</h4>
            <div className="space-y-2">
              {pricing.note.split('\n').map((paragraph, index) => (
                <p
                  key={index}
                  className="text-[16px] text-gray-600 leading-relaxed whitespace-pre-wrap break-words"
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>
    </Popup>
  );
};